import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans, t, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Stack,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Slide,
  Snackbar,
  Typography,
  useTheme } from
'@mui/material';
// import { AddLocationOutlined, Close, EditLocationOutlined, Logout } from '@mui/icons-material'
import { Close } from '@mui/icons-material';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
// import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import { NAV_LAYOUT_MARGIN_START } from '../consts/nav-layout-margin';
import {
  buildOrderedCrashPayload,
  CRASH_FORM_SECTION_KEYS,
  MERGED_CRASH_CONFIGURATION_ORG_IDS,
  MERGED_CRASH_INIT_ORGANIZATION_ID,
  mergeFormConfigurations,
  mergeFormDefaults,
  orderCrashFormSectionKeys,
  resolveFormTypeFromPath,
} from '../utils/crash-form-sections';
import { buildNavTreeStructure } from '../utils/nav-tree-structure';
import DesktopNav from '../components/desktop/DesktopNav';
import NaviTreeView from '../components/forms/NaviTree';
import MobileTopNav, { MobileTopNavSpacer } from '../components/mobile/MobileTopNav';
import MobileBottomNav from '../components/mobile/MobileBottomNav';
import useHttp from '../hooks/useHttp';
import useLogout from '../hooks/useLogout';
import FormContainer from '../components/forms/FormContainer';
import MobileLayersPanel from '../components/mobile/MobileLayers';
import Legend from '../components/Legend';
import FullPageProgress from '../components/FullPageProgress';
// import MapLibre from '../components/maps/MapLibre'
import MapLibre from '../components/maps/MapLibre_REACT';
import OfflineHomeMapPlaceholder from '../components/offline/offline-home-map-placeholder';
import dayjs from 'dayjs';
import LocalPolice from '@mui/icons-material/LocalPolice';
import LocalHospital from '@mui/icons-material/LocalHospital';
import AccountBalance from '@mui/icons-material/AccountBalance';
import History from './History';
import { canUserAccessActionHistory } from './Statistics/statistics-report-type';
import { buildConfigLocalizedUrl, getConfigLocaleHeaders, resolveSectionDisplayLabel } from '../utils/config-localization';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import useOfflineSync from '../hooks/useOfflineSync';
import { getInitSnapshot, saveInitSnapshot } from '../offline/init-snapshot';
import { initOfflineDataSync } from '../offline/offline-data-sync';
import { getAccessTokenFromStorage } from '../offline/session-store';
import {
  hasValidPersistedSession,
  isOfflineSessionUser,
  resolveSessionInit,
  resolveSessionToken,
} from '../offline/offline-session-utils';
import {
  upsertLocalIncident,
  listLocalIncidents,
  markIncidentExported,
  generateClientUuid } from
'../offline/incident-store';
import { enqueueFinalSync } from '../offline/sync-queue-helpers';
import {
  buildManifestJson,
  getCrashEntryStem,
  prepareCrashPayloadForZipExport,
  resolveSingleSourceOrganization,
  sanitizeCrashEntryFilename } from
'../offline/crash-package-export';
import { filterLocalIncidentsForExport } from '../offline/offline-export-filter';
import {
  canCreateOfflineCrash,
  canExportLocalCrashData,
} from '../offline/offline-field-permissions';
import { canProvisionDevice } from '../offline/device-provision-meta';
import { useRegisterOfflineNavActions } from '../contexts/offline-nav-actions-context';
import { ThemeContext } from '../App';
import httpReq from '../ce-services/http-req';
import DesktopToolbar from '../components/desktop/DesktopToolbar';
import { ColorPalette } from "../consts/ColorPalette";
import { decodeMapLayerFileContent } from '../utils/decode-map-layer-file-content';
import {
  enrichMapLayersForUi,
  mergeMapLayersFromApi } from
'../utils/map-layers';
import { stampCrashSectionDates } from '../utils/api-dates';
import { normalizeCrashPayloadDates } from '../utils/crash-payload-dates';
import { NewApiBaseUrl } from "../consts/new-api-base-url";
import useHttpApiNew from "../hooks/useHttpApiNew";
import {
  RISK_MAP_BOUNDS_DEBOUNCE_MS,
  RISK_MAP_DEMO_SEED_COUNT,
  RISK_MAP_DEMO_SEED_ENABLED,
  RISK_MAP_DEMO_SEED_MAX,
  RISK_MAP_ENABLED,
  RISK_MAP_NETWORK_MIN_ZOOM,
  buildRiskMapYearFilters,
} from '../shared/constants/risk-map';
import {
  buildStatsRequestPayload,
  extractYearlySummaryFromStatsResponse,
  normalizeYearlySummaryRow,
} from '../components/data-analysis/charts/stats-response-utils';
import {
  buildSegmentRiskScoreMap,
} from '../utils/road-risk-map-trend';
import { enrichStatsRequestPayload } from './Statistics/statistics-dataset-context';
import {
  getDefaultStatsReportType,
  isAnalysisOnlyAnalystUser,
} from './Statistics/statistics-report-type';
import {
  fetchCrashValidatedSummary,
  loadCachedCrashesSummary,
  normalizeCrashesSummary,
  saveCachedCrashesSummary,
} from '../services/crash-validated-summary.service';
import SvgCustomIcon from "../components/icons/SvgCustomIcon";
import { useMap } from '@vis.gl/react-maplibre';
import { useActionLogs } from '../store/actionLogsContext';
import { postCrashZipImport } from '../services/crash-zip-import';
import { postPackageExportRegistration } from '../services/crash-package-exports';
import {
  compressCrashImageDataUrl,
  compressCrashImages,
} from '../utils/crash-image-compress';

function persistGpsMetaFromPosition(pos) {
  try {
    localStorage.setItem(
      'adams_last_gps_meta',
      JSON.stringify({
        accuracy: pos.coords.accuracy,
        capturedAt: new Date().toISOString(),
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude
      })
    );
  } catch {

    /* ignore */}
}

async function checkGpsPermission() {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'unknown';
  }
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state;
  } catch {
    return 'unknown';
  }
}

const newAddCrashUrl = new URL(`${NewApiBaseUrl}/crash`);
const newModifyCrashUrl = new URL(`${NewApiBaseUrl}/crash`);
const newAdminModifyCrashUrl = new URL(`${NewApiBaseUrl}/AdminCrash`);
// /ID DELL'INCIDENTE
const newDeleteCrashUrl = new URL(`${NewApiBaseUrl}/crash/delete`);
// /ID DELL'INCIDENTE
const getSingleCrashUrl = new URL(`${NewApiBaseUrl}/crash`);
const getAllCrashesUrl = new URL(`${NewApiBaseUrl}/crash/all`);
const getRiskMapUrl = new URL(`${NewApiBaseUrl}/Stats/risk-map`);
const getRoadNetworkUrl = new URL(`${NewApiBaseUrl}/Stats/road-network`);
const getYearlySummaryUrl = new URL(`${NewApiBaseUrl}/Stats/yearly-summary`);
const getRiskMapDemoSeedUrl = new URL(`${NewApiBaseUrl}/Stats/risk-map/demo-seed`);
const insertDraftNewUrl = new URL(`${NewApiBaseUrl}/draft`);
const getSingleDraftUrl = new URL(`${NewApiBaseUrl}/Draft`);
const getDraftsListUrl = new URL(`${NewApiBaseUrl}/Draft`);

// eslint-disable-next-line no-restricted-globals
// const allDraftsUrl = new URL('/drafts', location)
const initUrl = new URL(`${NewApiBaseUrl}/Auth/Init`);
const getSingleUserUrl = new URL(`${NewApiBaseUrl}/Auth/Get`);

/**
 * @param {{ responseData?: unknown }} [response]
 * @returns {string | null}
 */
function extractServerCrashIdFromResponse(response) {
  const data = response?.responseData;
  if (data == null) return null;
  if (typeof data === 'string') return data;
  if (typeof data === 'object') {
    const record = /** @type {Record<string, unknown>} */ (data);
    const crashId = record.crash_id ?? record['Crash id'];
    return typeof crashId === 'string' && crashId !== '' ? crashId : null;
  }
  return null;
}

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

const FORM_SECTION_KEYS = CRASH_FORM_SECTION_KEYS;

/** Id utente accettato da Auth/Get (evita -1 e altri sentinel usati nei log / backend). */
function isValidUserIdForAuthGet(id) {
  if (id === undefined || id === null || id === '') return false;
  const n = Number(id);
  return !Number.isNaN(n) && n > 0;
}

/** Returns the section key from formModel that matches the given canonical name (case-insensitive). */
function getSectionKey(formModel, canonical) {
  const lower = canonical.toLowerCase();
  return Object.keys(formModel || {}).find((k) => k.toLowerCase() === lower) ?? null;
}

/** Returns the list of field labels (keys) allowed for a section by the form init configuration. */
function getSectionFieldLabels(formModel, section) {
  const key = typeof section === 'string' ? getSectionKey(formModel, section) : section;
  const elements = key ? formModel?.[key]?.elements : undefined;
  if (!Array.isArray(elements)) return [];
  return elements.map((e) => e?.label).filter(Boolean);
}

/** Picks from obj only the keys that are in allowedKeys. */
function pickKeys(obj, allowedKeys) {
  if (obj == null) return obj;
  return allowedKeys.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(obj, key)) acc[key] = obj[key];
    return acc;
  }, {});
}

/**
 * Builds form list from crash/draft data.
 * Primary filter: `allowedKeys` (usually org init `configuration` keys from getNewInit).
 * Fallback: any `FORM_SECTION_KEYS` section that exists in `formObj` with data but is missing from
 * `allowedKeys` — otherwise ZIP/API payloads with `people` / `units` are omitted and toolbar People/Units
 * navigation gets `formList.indexOf(...) === -1`.
 */
function composeFormList(formObj, allowedKeys = FORM_SECTION_KEYS) {
  const keys = orderCrashFormSectionKeys(
    Object.keys(formObj).filter((k) => {
      if (allowedKeys.includes(k)) return true;
      if (!FORM_SECTION_KEYS.includes(k)) return false;
      const v = formObj[k];
      if (v == null) return false;
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'object') return Object.keys(v).length > 0;
      return false;
    }),
    orderCrashFormSectionKeys(allowedKeys, FORM_SECTION_KEYS),
  );
  let formList = [];
  keys.forEach((key) => {
    if (Array.isArray(formObj[key])) {
      formObj[key].forEach((item, i) => {
        formList = [...formList, `${key}.${i}`];
        Object.keys(item).forEach((elem) => {
          if (Array.isArray(item[elem])) {
            item[elem].forEach((_, j) => {
              formList = [...formList, `${key}.${i}.${elem}.${j}`];
            });
          }
        });
      });
    } else {
      formList = [...formList, key];
    }
  });
  return formList;
}

/**
 * Per i nuovi report, le sezioni "multi" devono partire vuote:
 * unità/persone vengono aggiunte esplicitamente con i pulsanti toolbar.
 * Manteniamo comunque `defaults` originali per clonare il template al momento di append.
 */
function normalizeInitialSectionValue(configuration, sectionKey, sectionValue) {
  const sectionConfig = configuration?.[sectionKey];
  const isMultiSection = sectionConfig?.multi === true;
  if (isMultiSection && Array.isArray(sectionValue)) {
    return [];
  }
  return sectionValue;
}

/**
 * Primo paint con configuration valida: stato route, altrimenti `adams_user.init`
 * (offline / navigazione senza `location.state` evita form vuoto finché l’effect async non gira).
 */
function getHydratedInitFirstPaint(location) {
  return resolveSessionInit(location?.state) ?? location?.state?.init ?? null;
}

export default function Home({ position, isMobile, handledCrash, themeMode, setThemeMode, resetHandledCrash }) {
  const isOnline = useOnlineStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => resolveSessionToken(location.state), [location.state]);
  const isOfflineSession = useMemo(() => {
    if (token === '__offline__') return true;
    return isOfflineSessionUser();
  }, [token]);
  /** Drafts API non disponibili in inserimento offline (rete assente o login offline). */
  const showDraftsInAddDialog = isOnline && !isOfflineSession;
  const [hydratedInit, setHydratedInit] = useState(() => getHydratedInitFirstPaint(location));
  const init = useMemo(() => hydratedInit || {}, [hydratedInit]);
  const logout = useLogout();
  const theme = useTheme();

  useEffect(() => {
    if (!token) return undefined;
    if (typeof navigator === 'undefined' || !navigator.onLine) return undefined;
    initOfflineDataSync().catch(() => {});
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    async function hydrateInit() {
      if (location.state?.init?.configuration) {
        setHydratedInit(location.state.init);
        await saveInitSnapshot(location.state.init).catch(() => {});
        return;
      }
      // Dopo login online `adams_user.init` è sempre aggiornato; lo snapshot IndexedDB può restare
      // dell’utente precedente (es. admin) se il logout non lo cancella — non usarlo prima dello storage.
      try {
        const u = JSON.parse(localStorage.getItem('adams_user') || 'null');
        if (!cancelled && u?.init?.configuration) {
          setHydratedInit(u.init);
          await saveInitSnapshot(u.init).catch(() => {});
          return;
        }
      } catch {

        /* ignore */}
      const snap = await getInitSnapshot();
      if (!cancelled && snap?.init?.configuration) {
        setHydratedInit(snap.init);
        return;
      }
    }
    hydrateInit();
    return () => {
      cancelled = true;
    };
  }, [location.state]);
  const { _ } = useLingui();
  const methods = useForm({
    defaultValues: useMemo((elements) => elements, [])
  });
  const unitsFieldArrayMethods = useFieldArray({
    control: methods.control,
    name: 'units',
    shouldUnregister: true
  });
  const peopleFieldArrayMethods = useFieldArray({
    control: methods.control,
    name: 'people',
    shouldUnregister: true
  });
  const { isLoading, error, sendRequest, setError } = useHttp();
  const [user, setUser] = useState({});
  const [totCrashes, setTotCrashes] = useState(() => loadCachedCrashesSummary() ?? { total: 0 });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addDialogStep, setAddDialogStep] = useState('choose_org');
  const [selectedOrgIdFromDialog, setSelectedOrgIdFromDialog] = useState(null);
  const [isAdminDialogOpen, setIsAdminDialogOpen] = useState(false);
  const [isPositioning, setIsPositioning] = useState(false);
  const [isPositionSet, setIsPositionSet] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isActionLogDrawerOpen, setIsActionLogDrawerOpen] = useState(false);
  const [isAdminModifyValidated, setIsAdminModifyValidated] = useState(false);
  const isHistoryOpen = location.state?.isHistoryOpen ?? false;
  const sessionUser = useMemo(
    () => (user?.type != null ? user : init?.user),
    [user, init?.user],
  );
  const canAccessHistory = useMemo(
    () => canUserAccessActionHistory(sessionUser),
    [sessionUser],
  );
  const effectiveHistoryOpen = canAccessHistory && isHistoryOpen;

  useEffect(() => {
    if (!canAccessHistory && isHistoryOpen) {
      navigate('/home', {
        state: { ...(location.state || {}), isHistoryOpen: false },
        replace: true,
      });
    }
  }, [canAccessHistory, isHistoryOpen, navigate, location.state]);
  const [isStatsPanelOpen, setIsStatsPanelOpen] = useState(false);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [isDraftsMenuOpen, setIsDraftsMenuOpen] = useState(false);
  const [allDrafts, setAllDrafts] = useState([]);
  const [allAutoDrafts, setAllAutoDrafts] = useState([]);
  const [allCrashes, setAllCrashes] = useState([]);
  const [showCrashes, setShowCrashes] = useState(false);
  const [isMerged, setIsMerged] = useState(false);
  const [isUnmerged, setIsUnmerged] = useState(false);
  const [message, setMessage] = useState('');
  const [openMobileLayers, setOpenMobileLayers] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  // const [showHeatMap, setShowHeatMap] = useState(false)
  const [heatMapData, setHeatMapData] = useState(null);
  const [formNumber, setFormNumber] = useState(0);
  const [formModel, setFormModel] = useState({});
  const [formList, setFormList] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [initFormObj, setInitFormObj] = useState({});
  const [images, setImages] = useState([]);
  const [newCrash, setNewCrash] = useState(null);
  const [ownPosCoords, setOwnPosCoords] = useState(null);
  // const [crashInfo, setCrashInfo] = useState({})
  const [reporterData, setReporterData] = useState({});
  const submitRef = useRef();
  const offlineClientUuidRef = useRef(null);
  // const mapRef = useRef()
  const { toggleTheme } = useContext(ThemeContext);
  const [publicCrashList, setPublicCrashList] = useState([]);
  const [safetyIssuesList, setSafetyIssuesList] = useState([]);
  const [isPublicCrashVis, setIsPublicCrashVis] = useState(false);
  const [showSafetyIssues, setShowSafetyIssues] = useState(false);
  const canDrawPolygon = useMemo(() => {
    const userType = user?.type ?? init?.user?.type;
    const org = user?.organization ?? init?.user?.organization;
    const orgIds = Array.isArray(user?.organization_ids) ? user.organization_ids : Array.isArray(init?.user?.organization_ids) ? init.user.organization_ids : [];
    return userType === 1 || Number(org) === 1 || orgIds.some((id) => Number(id) === 1);
  }, [user?.type, user?.organization, user?.organization_ids, init?.user?.type, init?.user?.organization, init?.user?.organization_ids]);
  const [layersList, setLayersList] = useState([]);
  const [isMapLayerReordering, setIsMapLayerReordering] = useState(false);
  const { request } = useHttpApiNew();
  useOfflineSync(request);

  const loadValidatedSummary = useCallback(async () => {
    if (!token || token === '__offline__') return;

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const cached = loadCachedCrashesSummary();
      if (cached) setTotCrashes(cached);
      return;
    }

    try {
      const data = await fetchCrashValidatedSummary(request, token);
      const normalized = normalizeCrashesSummary(data);
      setTotCrashes(normalized);
      saveCachedCrashesSummary(data);
    } catch {
      const cached = loadCachedCrashesSummary();
      if (cached) setTotCrashes(cached);
    }
  }, [token, request]);

  useEffect(() => {
    if (location.pathname === '/home' && token) {
      loadValidatedSummary();
    }
  }, [location.pathname, token, loadValidatedSummary]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        location.pathname === '/home' &&
        token
      ) {
        loadValidatedSummary();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [location.pathname, token, loadValidatedSummary]);
  const { fetchUnreadCount } = useActionLogs();

  useEffect(() => {
    if (isFormOpen) {
      if (!offlineClientUuidRef.current) {
        offlineClientUuidRef.current = generateClientUuid();
      }
    } else {
      offlineClientUuidRef.current = null;
    }
  }, [isFormOpen]);

  const mapRef = useRef();
  const [severity, setSeverity] = useState('error');
  const [isAcquiringGps, setIsAcquiringGps] = useState(false);
  const [isOfflineSaving, setIsOfflineSaving] = useState(false);
  const hasFlownRef = useRef(false); // Flag per evitare flyTo multipli
  const shouldTriggerGeoControl = useRef(true); // Flag per controllare il trigger del GeolocateControl
  /** user_id dall'ultima GET incidente (`/crash/:id`); va riportato nel body di salvataggio. */
  const crashUserIdFromGetRef = useRef(null);

  const [visibility, setVisibility] = useState({
    ...(RISK_MAP_ENABLED ? { analysis: false } : {}),
  });
  const [heatmapLayerOn, setHeatmapLayerOn] = useState(false);
  const [riskMapLayerOn, setRiskMapLayerOn] = useState(false);
  const [roadRiskMapData, setRoadRiskMapData] = useState(null);
  const [roadRiskMapMetadata, setRoadRiskMapMetadata] = useState(null);
  const [roadRiskMapPrevScores, setRoadRiskMapPrevScores] = useState(null);
  const [roadNetworkMapData, setRoadNetworkMapData] = useState(null);
  const [roadNetworkUnderlayEnabled, setRoadNetworkUnderlayEnabled] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const [riskMapLoading, setRiskMapLoading] = useState(false);
  const [riskMapYears, setRiskMapYears] = useState([]);
  /** Selected calendar years for Validated/Final/Analysis (multiselect). */
  const [mapFilterYears, setMapFilterYears] = useState([]);
  const [riskMapDemoSeedLoading, setRiskMapDemoSeedLoading] = useState(false);
  const [analysisRefreshNonce, setAnalysisRefreshNonce] = useState(0);
  const riskMapFetchSeqRef = useRef(0);
  const roadNetworkFetchSeqRef = useRef(0);
  const heatMapFetchSeqRef = useRef(0);
  const crashFetchSeqRef = useRef(0);
  const fetchRoadRiskMapRef = useRef(null);
  const fetchRoadNetworkUnderlayRef = useRef(null);

  const analysisAvailable = RISK_MAP_ENABLED;
  const analysisOn = Boolean(analysisAvailable && visibility.analysis);
  /** Heatmap / risk map require exactly one selected year. */
  const riskMapYear = mapFilterYears.length === 1 ? mapFilterYears[0] : null;

  const [layersVisibility, setLayersVisibility] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    {});

  const canReorderMapLayers = useMemo(() => {
    const userType = Number(user?.type ?? init?.user?.type);
    return userType === 1 || userType === 2 || userType === 3;
  }, [user?.type, init?.user?.type]);

  const isAnalysisOnlyAnalyst = useMemo(
    () => isAnalysisOnlyAnalystUser(sessionUser, init?.organizations ?? []),
    [sessionUser, init?.organizations],
  );

  const updateLayerById = useCallback((layerId, patch) => {
    setLayersList((prev) =>
    prev.map((l) => Number(l.id) === Number(layerId) ? { ...l, ...patch } : l)
    );
  }, []);

  const getLayersList = async () => {
    if (!isOnline) return;
    const res = await httpReq('MapLayer/GetList', 'GET', null, true);
    if (!res.responseAnyError && Array.isArray(res.responseData)) {
      setLayersList((prev) =>
      enrichMapLayersForUi(res.responseData, ColorPalette).map((layer) => {
        const existing = prev.find((l) => l.id === layer.id);
        if (!existing) return layer;
        return {
          ...layer,
          isOpen: existing.isOpen,
          isFocused: existing.isFocused,
          focusNonce: existing.focusNonce ?? 0,
          isLoading: existing.isLoading,
          isLoaded: existing.isLoaded,
          fileContent: existing.fileContent,
          color: existing.color ?? layer.color
        };
      })
      );
    }
  };

  const moveMapLayer = useCallback(
    async (layerId, direction) => {
      if (!isOnline) return;
      const path =
      direction === 'up' ?
      `MapLayer/MoveUp/${layerId}` :
      `MapLayer/MoveDown/${layerId}`;
      setIsMapLayerReordering(true);
      try {
        const res = await httpReq(path, 'POST', null, true);
        if (res.responseAnyError || !Array.isArray(res.responseData)) {
          setSeverity('error');
          setMessage(_(msg`Could not reorder map layer`));
          return;
        }
        setLayersList((prev) =>
        mergeMapLayersFromApi(res.responseData, prev, ColorPalette)
        );
      } finally {
        setIsMapLayerReordering(false);
      }
    },
    [_, isOnline]
  );

  const handleMoveLayerUp = useCallback(
    (layerId) => moveMapLayer(layerId, 'up'),
    [moveMapLayer]
  );

  const handleMoveLayerDown = useCallback(
    (layerId) => moveMapLayer(layerId, 'down'),
    [moveMapLayer]
  );

  useEffect(() => {
    if (!isOnline) return;
    layersList.forEach((layer) => {
      if (layer.isOpen === true) {
        if (layer.isLoaded === false && layer.isLoading === false) {
          const layerId = layer.id;
          updateLayerById(layerId, { isLoading: true });
          httpReq(`MapLayer/Get/${layerId}`, 'GET', null, true).then(async (response) => {
            if (response.responseAnyError) {
              updateLayerById(layerId, { isLoading: false });
              return;
            }
            const loaded = response.responseData;
            const featureCollection = await decodeMapLayerFileContent(loaded.fileContent);
            updateLayerById(layerId, {
              fileContent: featureCollection,
              isLoading: false,
              isLoaded: Boolean(featureCollection?.features?.length),
            });
          });
        }
      }
    });
  }, [layersList, isOnline, updateLayerById]);

  useEffect(() => {
    // Convert true/false to "visible"/"none" for map layers.
    setLayersVisibility({
      analysis: analysisOn ? 'visible' : 'none',
      heatmap: analysisOn && heatmapLayerOn ? 'visible' : 'none',
      riskMap: analysisOn && riskMapLayerOn ? 'visible' : 'none',
    });
  }, [analysisOn, heatmapLayerOn, riskMapLayerOn]);

  const onVisibilityChange = (name, value) => {
    setVisibility((prev) => ({ ...prev, [name]: value }));
  };

  const fetchRiskMapYears = useCallback(async () => {
    if (!isOnline || !init?.configuration) return;

    try {
      const chartField = {
        reportType: getDefaultStatsReportType(init?.user ?? user),
        target: 'crash',
        fieldName: 'Crash date',
      };
      const base = buildStatsRequestPayload({
        chartField,
        fields: init.configuration,
        filters: [],
      });
      const chartRequest = enrichStatsRequestPayload(base, {
        user: init?.user ?? user,
        organizations: init?.organizations ?? [],
        reportType: chartField.reportType,
      });

      const authHeaders = { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` };

      const fetchYearsForMerged = async (merged) => {
        const res = await request(
          getYearlySummaryUrl,
          'POST',
          { ...chartRequest, merged },
          authHeaders,
        );
        if (
          res == null
          || res.responseAnyError
          || (typeof res.responseCode === 'number' && res.responseCode !== 0)
        ) {
          return [];
        }
        return extractYearlySummaryFromStatsResponse(res.responseData)
          .map(normalizeYearlySummaryRow)
          .map((row) => row.year)
          .filter((y) => Number.isFinite(y) && y > 0);
      };

      const [validatedYears, finalYears] = await Promise.all([
        fetchYearsForMerged(1),
        fetchYearsForMerged(0),
      ]);

      const uniqueDesc = Array.from(new Set([...validatedYears, ...finalYears]))
        .sort((a, b) => b - a);

      if (uniqueDesc.length === 0) {
        // eslint-disable-next-line no-console
        console.warn('[risk-map] yearly-summary returned no years (validated + final)');
      }

      setRiskMapYears(uniqueDesc);
      setMapFilterYears((prev) => {
        const kept = (Array.isArray(prev) ? prev : []).filter((y) => uniqueDesc.includes(y));
        if (kept.length > 0) return kept;
        return uniqueDesc[0] != null ? [uniqueDesc[0]] : [];
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[risk-map] yearly-summary error', err);
    }
  }, [init, isOnline, request, token, user]);

  const fetchRoadRiskMap = useCallback(
    async (year) => {
      if (!analysisAvailable || !isOnline || !init?.configuration) return;
      const selectedYear = Number(year);
      if (!Number.isFinite(selectedYear) || selectedYear < 1) return;

      const seq = ++riskMapFetchSeqRef.current;
      setRiskMapLoading(true);

      const requestRiskMapForYear = async (targetYear) => {
        const chartField = {
          reportType: getDefaultStatsReportType(init?.user ?? user),
          target: 'crash',
          fieldName: 'Crash severity',
        };
        const base = buildStatsRequestPayload({
          chartField,
          fields: init.configuration,
          filters: buildRiskMapYearFilters(targetYear),
        });
        const chartRequest = enrichStatsRequestPayload(base, {
          user: init?.user ?? user,
          organizations: init?.organizations ?? [],
          reportType: chartField.reportType,
        });
        chartRequest.merged = 1;

        const res = await request(
          getRiskMapUrl,
          'POST',
          { chartRequest },
          { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` }
        );

        if (
          res == null
          || res.responseAnyError
          || (typeof res.responseCode === 'number' && res.responseCode !== 0)
          || res.responseData == null
        ) {
          return null;
        }
        return res.responseData;
      };

      try {
        const previousYear = selectedYear - 1;
        const canComparePrevious = Array.isArray(riskMapYears) && riskMapYears.includes(previousYear);

        const [currentData, previousData] = await Promise.all([
          requestRiskMapForYear(selectedYear),
          canComparePrevious ? requestRiskMapForYear(previousYear) : Promise.resolve(null),
        ]);

        if (seq !== riskMapFetchSeqRef.current) return;

        if (currentData == null) {
          // eslint-disable-next-line no-console
          console.warn('[risk-map] request failed or empty ApiCallResponse');
          setRoadRiskMapPrevScores(null);
          return;
        }

        setRoadRiskMapData({
          type: currentData?.type ?? 'FeatureCollection',
          features: Array.isArray(currentData?.features) ? currentData.features : [],
        });
        setRoadRiskMapMetadata(currentData?.metadata ?? null);
        setRoadRiskMapPrevScores(
          previousData ? buildSegmentRiskScoreMap(previousData) : null
        );
      } catch (err) {
        if (seq !== riskMapFetchSeqRef.current) return;
        // eslint-disable-next-line no-console
        console.warn('[risk-map] fetch error', err);
      } finally {
        if (seq === riskMapFetchSeqRef.current) setRiskMapLoading(false);
      }
    },
    [analysisAvailable, init, isOnline, request, riskMapYears, token, user]
  );

  const fetchRoadNetworkUnderlay = useCallback(
    async (bbox) => {
      if (!analysisAvailable || !isOnline || !bbox) return;

      const zoom = Number(bbox.zoom);
      if (!Number.isFinite(zoom) || zoom < RISK_MAP_NETWORK_MIN_ZOOM) {
        setRoadNetworkMapData(null);
        return;
      }

      const seq = ++roadNetworkFetchSeqRef.current;
      try {
        const { zoom: _z, ...bboxOnly } = bbox;
        const res = await request(
          getRoadNetworkUrl,
          'POST',
          { bbox: bboxOnly },
          { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` }
        );

        if (seq !== roadNetworkFetchSeqRef.current) return;
        if (
          res == null
          || res.responseAnyError
          || (typeof res.responseCode === 'number' && res.responseCode !== 0)
          || res.responseData == null
        ) {
          // Keep previous underlay on transient failures.
          return;
        }

        const data = res.responseData;
        setRoadNetworkMapData({
          type: data?.type ?? 'FeatureCollection',
          features: Array.isArray(data?.features) ? data.features : [],
        });
      } catch (err) {
        if (seq !== roadNetworkFetchSeqRef.current) return;
        // eslint-disable-next-line no-console
        console.warn('[road-network] fetch error', err);
      }
    },
    [analysisAvailable, isOnline, request, token]
  );

  fetchRoadRiskMapRef.current = fetchRoadRiskMap;
  fetchRoadNetworkUnderlayRef.current = fetchRoadNetworkUnderlay;

  const refreshRiskMapLayers = useCallback(() => {
    if (riskMapYear != null) {
      fetchRoadRiskMapRef.current?.(riskMapYear);
    }
    if (roadNetworkUnderlayEnabled && mapBounds) {
      fetchRoadNetworkUnderlayRef.current?.(mapBounds);
    }
    setAnalysisRefreshNonce((n) => n + 1);
  }, [mapBounds, riskMapYear, roadNetworkUnderlayEnabled]);

  const seedRiskMapDemoCrashes = useCallback(async (count) => {
    if (!RISK_MAP_DEMO_SEED_ENABLED || !analysisAvailable || !isOnline) return;
    if (riskMapYear == null || !mapBounds) {
      // eslint-disable-next-line no-console
      console.warn('[risk-map] demo seed requires selected year and current map bounds');
      return;
    }

    const requested = Number(count);
    const seedCount = Number.isFinite(requested) && requested > 0
      ? Math.min(RISK_MAP_DEMO_SEED_MAX, Math.max(1, Math.round(requested)))
      : RISK_MAP_DEMO_SEED_COUNT;

    setRiskMapDemoSeedLoading(true);
    try {
      const { zoom, ...bboxOnly } = mapBounds;
      const res = await request(
        getRiskMapDemoSeedUrl,
        'POST',
        {
          year: riskMapYear,
          bbox: bboxOnly,
          count: seedCount,
        },
        { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` }
      );

      if (
        res == null
        || res.responseAnyError
        || (typeof res.responseCode === 'number' && res.responseCode !== 0)
      ) {
        // eslint-disable-next-line no-console
        console.warn('[risk-map] demo seed failed', {
          responseCode: res?.responseCode,
          responseMessage: res?.responseMessage,
        });
        return;
      }

      // eslint-disable-next-line no-console
      console.info('[risk-map] demo seed ok', res.responseData);
      await fetchRiskMapYears();
      fetchRoadRiskMapRef.current?.(riskMapYear);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[risk-map] demo seed error', err);
    } finally {
      setRiskMapDemoSeedLoading(false);
    }
  }, [
    analysisAvailable,
    fetchRiskMapYears,
    isOnline,
    mapBounds,
    request,
    riskMapYear,
    token,
  ]);

  const handleMapBoundsChange = useCallback((next) => {
    setMapBounds((prev) => {
      if (
        prev
        && Math.abs(prev.lat0 - next.lat0) < 1e-7
        && Math.abs(prev.lat1 - next.lat1) < 1e-7
        && Math.abs(prev.lon0 - next.lon0) < 1e-7
        && Math.abs(prev.lon1 - next.lon1) < 1e-7
        && Math.abs((prev.zoom ?? 0) - (next.zoom ?? 0)) < 1e-4
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!analysisAvailable && visibility.analysis) {
      setVisibility((prev) => ({ ...prev, analysis: false }));
    }
  }, [analysisAvailable, visibility.analysis]);

  useEffect(() => {
    if (!isOnline || !init?.configuration) {
      setRiskMapYears([]);
      setMapFilterYears([]);
      return undefined;
    }
    fetchRiskMapYears();
    return undefined;
  }, [isOnline, init?.configuration, fetchRiskMapYears]);

  // Multi-year selection disables heatmap and road risk layers.
  useEffect(() => {
    if (mapFilterYears.length === 1) return undefined;
    setHeatmapLayerOn(false);
    setRiskMapLayerOn(false);
    return undefined;
  }, [mapFilterYears]);

  // Country-wide risk map: refetch only when analysis is on or selected year changes.
  useEffect(() => {
    if (!analysisOn) {
      setRoadRiskMapData(null);
      setRoadRiskMapMetadata(null);
      setRoadRiskMapPrevScores(null);
      setRoadNetworkMapData(null);
      setRoadNetworkUnderlayEnabled(false);
      setRiskMapLoading(false);
      setHeatmapLayerOn(false);
      setRiskMapLayerOn(false);
      setHeatMapData(null);
      return undefined;
    }
    if (riskMapYear == null) {
      setRoadRiskMapData(null);
      setRoadRiskMapMetadata(null);
      setRoadRiskMapPrevScores(null);
      setRiskMapLoading(false);
      return undefined;
    }

    fetchRoadRiskMapRef.current?.(riskMapYear);

    return () => {
      riskMapFetchSeqRef.current += 1;
      setRiskMapLoading(false);
    };
  }, [analysisOn, riskMapYear]);

  // Road-network underlay: only when explicitly enabled from the toolbox (zoom-gated).
  useEffect(() => {
    if (!analysisOn || !roadNetworkUnderlayEnabled) {
      setRoadNetworkMapData(null);
      return undefined;
    }
    if (!mapBounds) return undefined;

    const timer = setTimeout(() => {
      fetchRoadNetworkUnderlayRef.current?.(mapBounds);
    }, RISK_MAP_BOUNDS_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      roadNetworkFetchSeqRef.current += 1;
    };
  }, [analysisOn, mapBounds, roadNetworkUnderlayEnabled]);



  const setFormModelConfiguration = useCallback(
    (configuration, defaults) => {
      setFormModel(configuration);
      /** Tutte le sezioni in ordine di config (non solo hierarchy === 0: units è 1, people è 2). */
      const orderedSectionKeys = Object.keys(configuration).
      sort((a, b) => configuration[a].position - configuration[b].position);
      const hierarchyZeroKeys = orderedSectionKeys.filter((k) => configuration[k].hierarchy === 0);

      const safeDefaults = defaults ?? {};
      const sortDefaults = Object.keys(safeDefaults).
      sort().
      reduce((acc, nextKey) => ({ ...acc, [nextKey]: safeDefaults[nextKey] }), {});
      setDefaults(sortDefaults);
      const formObj = {};
      orderedSectionKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(safeDefaults, key)) {
          formObj[key] = normalizeInitialSectionValue(configuration, key, safeDefaults[key]);
        }
      });
      if (formObj.crash) {
        formObj.crash = stampCrashSectionDates(formObj.crash);
      }
      const formObjWithSubmitFlags = {
        ...formObj,
        isFinal: false,
        isDraft: false
      };
      const allowedKeys = Object.keys(configuration);
      const expandedFormList = composeFormList(formObjWithSubmitFlags, allowedKeys);
      setFormList(expandedFormList.length > 0 ? expandedFormList : hierarchyZeroKeys);
      // Theese caches and sets the defaults of the form
      setInitFormObj(formObjWithSubmitFlags);
      methods.reset(formObjWithSubmitFlags); // {defaults.crash, road: defaults.road }
    },
    [methods]
  );

  // Fetch of init
  useEffect(() => {
    if (!token) {
      if (!hasValidPersistedSession()) logout();
      return;
    }
    if (!init?.configuration) return;
    const { configuration, defaults, user } = init;
    setUser((prev) => {
      const incoming = { ...user };
      const isSystemAdmin = incoming?.type === 1 || prev?.type === 1;
      if (
      isSystemAdmin &&
      prev?.doAsOrganization != null &&
      prev.doAsOrganization !== -1 && (
      incoming.organization === -1 || incoming.organization == null))
      {
        incoming.doAsOrganization = prev.doAsOrganization;
      }
      return incoming;
    });
    // setThemeMode(user.theme)
    // Con form aperto (o minimizzato su mobile: isPositionSet resta true) NON fare reset ai default:
    // altrimenti minimize → setFormModelConfiguration azzera formList/units e alla riapertura
    // formList[formNumber] è undefined → crash su form.name.match.
    // dopo getCrash → getNewInit aggiorna `init` e questo effect altrimenti eseguirebbe
    // setFormModelConfiguration → methods.reset(defaults) e cancellerebbe Name/Surname ecc.
    if (isFormOpen || isPositionSet) {
      setFormModel(configuration);
      const safeDefaults = defaults ?? {};
      const sortDefaults = Object.keys(safeDefaults).
      sort().
      reduce((acc, nextKey) => ({ ...acc, [nextKey]: safeDefaults[nextKey] }), {});
      setDefaults(sortDefaults);
      return;
    }
    setFormModelConfiguration(configuration, defaults);
  }, [token, init, logout, setFormModelConfiguration, toggleTheme, isFormOpen, isPositionSet]);

  // Close and reset form when navigating away from /home
  useEffect(() => {
    // Only close form if we're navigating away from /home and form is open
    if (location.pathname !== '/home' && isFormOpen) {
      setIsPositionSet(false);
      setIsFormOpen(false);
      setIsAdminModifyValidated(false);
      setIsPositioning(false);
      setNewCrash(null);
      crashUserIdFromGetRef.current = null;
      methods.reset(initFormObj);
      unitsFieldArrayMethods.remove();
      peopleFieldArrayMethods.remove();
      setImages(() => []);
      setFormNumber(0);
    }
  }, [location.pathname, isFormOpen, initFormObj, methods, unitsFieldArrayMethods, peopleFieldArrayMethods]);

  // On mobile, close the layers section whenever the form is open
  useEffect(() => {
    if (isMobile && isFormOpen) {
      setOpenMobileLayers(false);
    }
  }, [isMobile, isFormOpen]);

  const fetchOrganizationInit = useCallback(async (organization) => {
    const newInitUrl = buildConfigLocalizedUrl(initUrl, { organization });
    try {
      const response = await fetch(newInitUrl, {
        method: 'GET',
        headers: getConfigLocaleHeaders({
          Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}`,
        }),
      }).then((res) => res.json());
      if (response?.responseAnyError) return null;
      const { error: initError, error_code, result, ...newInit } = response?.responseData ?? response ?? {};
      if (initError || !newInit.configuration) return null;
      return newInit;
    } catch (e) {
      return null;
    }
  }, [token]);

  const applyOrganizationInitToForm = useCallback((newInit, organization) => {
    const resolvedOrgId =
    newInit.user?.organization != null && newInit.user.organization !== -1 ?
    newInit.user.organization :
    organization;
    setSelectedOrgIdFromDialog(resolvedOrgId);
    setUser((old) => ({
      ...old,
      doAsOrganization: resolvedOrgId,
      doAsBasicOrganization: newInit.user?.basic_organization ?? old?.doAsBasicOrganization
    }));
    setFormModelConfiguration(newInit.configuration, newInit.defaults);
    setHydratedInit((prev) => {
      const next = {
        ...(prev || {}),
        configuration: newInit.configuration,
        defaults: newInit.defaults,
        user: newInit.user != null ? { ...newInit.user } : prev?.user
      };
      saveInitSnapshot(next).catch(() => {});
      return next;
    });
    return {
      configuration: newInit.configuration,
      defaults: newInit.defaults,
      organizationId: resolvedOrgId
    };
  }, [setFormModelConfiguration, setHydratedInit]);

  const getNewInit = useCallback(async (organization) => {
    const newInit = await fetchOrganizationInit(organization);
    if (!newInit) return null;
    return applyOrganizationInitToForm(newInit, organization);
  }, [fetchOrganizationInit, applyOrganizationInitToForm]);

  const getMergedCrashInit = useCallback(async () => {
    const inits = await Promise.all(
      MERGED_CRASH_CONFIGURATION_ORG_IDS.map((orgId) => fetchOrganizationInit(orgId)),
    );
    const validInits = inits.filter(Boolean);
    if (validInits.length === 0) return null;

    const mergedConfiguration = mergeFormConfigurations(
      validInits.map((initPayload) => initPayload.configuration),
    );
    const mergedDefaults = mergeFormDefaults(
      validInits.map((initPayload) => initPayload.defaults),
    );
    const policeInit = validInits.find(
      (_, index) => MERGED_CRASH_CONFIGURATION_ORG_IDS[index] === MERGED_CRASH_INIT_ORGANIZATION_ID,
    ) ?? validInits[0];

    return applyOrganizationInitToForm({
      ...policeInit,
      configuration: mergedConfiguration,
      defaults: mergedDefaults,
    }, MERGED_CRASH_INIT_ORGANIZATION_ID);
  }, [fetchOrganizationInit, applyOrganizationInitToForm]);

  const getAllDrafts = useCallback(async () => {
    if (!token) return logout();
    if (!isAddDialogOpen) return;
    const response = await request(getDraftsListUrl, 'GET', null, { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` });
    if (response.responseAnyError) return;
    if (response.responseData.length > 0) {
      setAllAutoDrafts(
        response.responseData.
        filter((draft) => draft.endsWith('_auto')).
        sort((a, b) => {
          // Estrai la data dal nome del draft (formato: 1_20251209T154949208_auto)
          const dateA = a.split('_')[1];
          const dateB = b.split('_')[1];
          return dateB.localeCompare(dateA); // Ordine decrescente
        }).
        filter((_, i) => i < 5)
      );

      setAllDrafts(
        response.responseData.
        filter((draft) => !draft.endsWith('_auto')).
        sort((a, b) => {
          // Estrai la data dal nome del draft
          const dateA = a.split('_')[1];
          const dateB = b.split('_')[1];
          return dateB.localeCompare(dateA); // Ordine decrescente
        }).
        filter((_, i) => i < 5)
      );
    }
    /*
    sendRequest({
      token
      , url: allDraftsUrl
      , manageResData: res => {
        }
    })
     */

  }, [token, isAddDialogOpen, logout]);

  const getDraft = useCallback(
    async (draftId) => {
      if (!token) return logout();
      crashUserIdFromGetRef.current = null;
      const getSingleDraftUrlCompose = getSingleDraftUrl + `/${draftId}`;
      const response = await request(getSingleDraftUrlCompose, 'GET', null, { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` });
      if (response.responseAnyError) return;
      const data = response.responseData;
      if (Object.prototype.hasOwnProperty.call(data, 'crash_id')) {
        delete data.crash_id;
      }
      if (Object.prototype.hasOwnProperty.call(data, 'isDraft')) {
        delete data.isDraft;
      }
      if (Object.prototype.hasOwnProperty.call(data, 'isFinal')) {
        delete data.isFinal;
      }
      const draftAllowedKeys = Object.keys(init?.configuration || {});
      const draftFormsList = composeFormList(data, draftAllowedKeys.length ? draftAllowedKeys : undefined);
      setFormList(draftFormsList);
      methods.reset(data); // Resets form
      closeDialog();
      setNewCrash(data.crash);
      // setCrashInfo(data.crash)
      setImages(data.crash.images);
      setIsFormOpen(true);
    },
    [token, logout, methods]
  );

  const getCrash = useCallback(
    async (crashId, organization, reporterId, options) => {
      if (!token) return logout();
      const adminModifyValidated = options?.adminModifyValidated ?? false;

      setReporterData({});
      //  const getCrashUrl = new URL(getCrashBaseUrl)
      // getCrashUrl.pathname += `/${crashId}`
      const res = await request(getSingleCrashUrl + '/' + crashId, 'GET', null, { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` });
      if (res.responseAnyError || !res.responseData.crashes) {
        setMessage(res.responseAnyError ? t`Error loading crash.` : t`Crash not found or merged with another crash.`);
        return;
      }
      const crash = res.responseData.crashes[0];
      const rawUserIdFromGet =
      crash?.user_id ??
      crash?.userId ??
      crash?.crash?.user_id ??
      crash?.crash?.userId;
      crashUserIdFromGetRef.current =
      rawUserIdFromGet !== undefined && rawUserIdFromGet !== null && rawUserIdFromGet !== '' ?
      rawUserIdFromGet :
      null;

      const reporterUserIdToFetch = isValidUserIdForAuthGet(rawUserIdFromGet) ?
      rawUserIdFromGet :
      isValidUserIdForAuthGet(reporterId) ?
      reporterId :
      null;
      if (reporterUserIdToFetch !== null) {
        const response = await request(`${getSingleUserUrl}/${reporterUserIdToFetch}`, 'GET');
        if (!response.responseAnyError && response.responseData) {
          setReporterData(response.responseData);
        }
      }
      // Disabilita il trigger automatico del GeolocateControl quando si apre un crash
      shouldTriggerGeoControl.current = false;
      // Fly to crash location solo se non è già stato fatto per questo crash
      // e solo se viene chiamato da CrashList (non da marker sulla mappa)
      if (mapRef.current && crash.crash.Longitude && crash.crash.Latitude && !hasFlownRef.current) {
        hasFlownRef.current = true;
        // Usa requestAnimationFrame per evitare conflitti con il rendering
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (mapRef.current && mapRef.current.getMap) {
              const map = mapRef.current.getMap();
              if (map && !map.isMoving()) {
                mapRef.current.flyTo({
                  center: [crash.crash.Longitude, crash.crash.Latitude],
                  speed: 2,
                  curve: 1
                });
                // Reset flag dopo l'animazione (circa 2 secondi)
                setTimeout(() => {
                  hasFlownRef.current = false;
                  // Riabilita il geolocate dopo il flyTo
                  shouldTriggerGeoControl.current = true;
                }, 2500);
              } else {
                hasFlownRef.current = false;
                shouldTriggerGeoControl.current = true;
              }
            }
          }, 300);
        });
      } else {
        // Se non facciamo flyTo, riabilita subito
        setTimeout(() => {
          shouldTriggerGeoControl.current = true;
        }, 500);
      }
      const orderedCrash = buildOrderedCrashPayload(crash);
      const isMergedValidated = Boolean(crash.crash?.merge_date);
      const crashOrg =
        organization && organization !== -1 ? organization : crash.crash.organization;
      let allowedKeys = FORM_SECTION_KEYS;
      if (isMergedValidated || init.user.type > 0) {
        const newInit = isMergedValidated ?
        await getMergedCrashInit() :
        await getNewInit(crashOrg);
        if (newInit?.configuration) allowedKeys = Object.keys(newInit.configuration);
      }
      const crashFormsList = composeFormList(orderedCrash, allowedKeys);
      setFormList(crashFormsList);
      methods.reset(orderedCrash); // Resets form
      // setCrashInfo(crash.crash)
      setNewCrash(crash.crash);
      setImages(crash.crash.images);
      setIsAdminModifyValidated(adminModifyValidated);
      setIsFormOpen(true);

    },
    [token, logout, methods, getNewInit, getMergedCrashInit, init.user.type]
  );

  useEffect(() => {
    if (Object.keys(handledCrash).length) {
      const { id, organization: orgName, reporterId, fromLockConfirm } = handledCrash;
      const organization = init.organizations.find((org) => org.label === orgName)?.id;
      getCrash(id, organization, reporterId, { adminModifyValidated: fromLockConfirm });
      // Reset handledCrash dopo aver chiamato getCrash per evitare chiamate multiple
      resetHandledCrash();
    }
  }, [handledCrash, getCrash, init.organizations, resetHandledCrash]);

  useEffect(() => {
    const openCrash = location.state?.openCrash;
    if (!openCrash?.id || !token) return;

    const organizationId =
      openCrash.organizationId != null && openCrash.organizationId !== ''
        ? Number(openCrash.organizationId)
        : undefined;
    const reporterId =
      openCrash.reporterId != null && openCrash.reporterId !== ''
        ? openCrash.reporterId
        : undefined;

    getCrash(String(openCrash.id), organizationId, reporterId);

    navigate('.', {
      replace: true,
      state: { ...(location.state || {}), openCrash: undefined },
    });
  }, [location.state?.openCrash, token, getCrash, navigate, location.state]);

  // Add crash flow controls
  const handleSetNewCrash = (crash) => {
    // Solo per nuovi incidenti azzeriamo il reporter dalla GET; se si aggiorna posizione
    // (drag marker) su un crash caricato, il ref deve restare quello dell'incidente.
    if (crash?.isNew) {
      crashUserIdFromGetRef.current = null;
    }
    setNewCrash(crash);
  };

  const openDialog = () => {
    setAddDialogStep('choose_org');
    setIsDraftsMenuOpen(false);
    setIsAddDialogOpen(true);
  };

  const closeDialog = () => {
    setAddDialogStep('choose_org');
    setIsDraftsMenuOpen(false);
    setIsAddDialogOpen(false);
    setSelectedOrgIdFromDialog(null);
  };

  const openAdminDialog = () => {
    setSelectedOrgIdFromDialog(null);
    setIsAdminDialogOpen(true);
  };

  const handleOpenAddCrash = useCallback(() => {
    const type = Number(user?.type ?? init?.user?.type);
    if (!isOnline && !canCreateOfflineCrash(type)) {
      setSeverity('warning');
      setMessage(
        _(
          msg`System administrators cannot add crash reports offline. Sign in online or use an organization account on this device.`
        )
      );
      return;
    }
    if (type === 1) {
      openAdminDialog();
    } else {
      openDialog();
    }
  }, [_, init?.user?.type, isOnline, user?.type]);

  /** Org scelta nel popover “Add crash” (admin / multi-org). */
  const resolveAddCrashOrganizationId = useCallback(() => {
    if (selectedOrgIdFromDialog != null && selectedOrgIdFromDialog !== '' && selectedOrgIdFromDialog !== -1) {
      return selectedOrgIdFromDialog;
    }
    const org = user?.organization ?? init?.user?.organization;
    if (org === -1) {
      return user?.doAsOrganization ?? init?.user?.doAsOrganization ?? null;
    }
    return user?.doAsOrganization ?? org ?? null;
  }, [selectedOrgIdFromDialog, user?.organization, user?.doAsOrganization, init?.user?.organization, init?.user?.doAsOrganization]);

  const stampOrganizationOnNewCrashForm = useCallback(() => {
    const orgId = resolveAddCrashOrganizationId();
    if (orgId != null && orgId !== '' && orgId !== -1) {
      methods.setValue('crash.organization', orgId);
    }
  }, [methods, resolveAddCrashOrganizationId]);

  useEffect(() => {
    if (isFormOpen && newCrash?.isNew) {
      stampOrganizationOnNewCrashForm();
    }
  }, [isFormOpen, newCrash, stampOrganizationOnNewCrashForm]);

  const closeAdminDialog = () => {
    setIsAdminDialogOpen(false);
  };

  const handlePositioningState = (state) => {
    setIsPositioning(state);
  };

  const handleIsPositionSetState = (state) => {
    setIsPositionSet(state);
  };

  const handleFormOpen = (state) => {
    setIsFormOpen(state);
  };

  const handleCoordinates = (coords) => {
    methods.setValue('crash.Latitude', coords.lat);
    methods.setValue('crash.Longitude', coords.lng);
  };

  const getCurrentGpsPosition = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy:
            position.coords.accuracy != null && !Number.isNaN(position.coords.accuracy) ?
            position.coords.accuracy :
            50
          });
        },
        (error) => {

          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    });
  }, []);

  const handleAddCrashOfflineWithGps = useCallback(async () => {
    setIsAcquiringGps(true);
    try {
      const permission = await checkGpsPermission();
      if (permission === 'denied') {
        setSeverity('error');
        setMessage(_(msg`GPS permission denied. Enable location in browser or system settings.`));
        closeDialog();
        crashUserIdFromGetRef.current = null;
        handleCoordinates({ lat: 0, lng: 0 });
        setNewCrash({ isNew: true, Latitude: 0, Longitude: 0 });
        setIsFormOpen(true);
        return;
      }

      const coords = await getCurrentGpsPosition();
      closeDialog();
      crashUserIdFromGetRef.current = null;
      handleCoordinates({ lat: coords.lat, lng: coords.lng });
      setNewCrash({ isNew: true, Latitude: coords.lat, Longitude: coords.lng });
      persistGpsMetaFromPosition({
        coords: {
          latitude: coords.lat,
          longitude: coords.lng,
          accuracy: coords.accuracy
        }
      });
      setSeverity('success');
      setMessage(_(msg`GPS position acquired`));
      setIsFormOpen(true);
    } catch (error) {

      closeDialog();
      crashUserIdFromGetRef.current = null;
      handleCoordinates({ lat: 0, lng: 0 });
      setNewCrash({ isNew: true, Latitude: 0, Longitude: 0 });
      setSeverity('warning');
      setMessage(_(msg`GPS not available, position set to 0,0`));
      setIsFormOpen(true);
    } finally {
      setIsAcquiringGps(false);
    }
  }, [_, closeDialog, getCurrentGpsPosition, handleCoordinates, setNewCrash, setIsFormOpen, setMessage, setSeverity]);

  const handleOwnPosCoords = (coords) => {
    setOwnPosCoords(coords);
  };

  const handlePicture = async (picture, removeIndex) => {
    if (removeIndex !== undefined) {
      setImages((old) => old.filter((o, i) => i !== removeIndex));
    } else {
      const compressed = await compressCrashImageDataUrl(picture);
      setImages((old) => [...old, compressed]);
    }
  };

  const handleCrashToEdit = (allCrashes, crashId) => {
    const allCrashesFiltered = { reduced: allCrashes.reduced.filter((ac) => ac['Crash id'] !== crashId) };
    setAllCrashes(allCrashesFiltered);
  };

  // Map overlays controls
  const toggleMobileLayers = (bool) =>
  setOpenMobileLayers(typeof bool !== 'boolean' ? !openMobileLayers : bool);

  useEffect(() => {
    if (!isOnline && openMobileLayers) {
      setOpenMobileLayers(false);
    }
  }, [isOnline, openMobileLayers]);

  useEffect(() => {
    if (!isOnline && isPositioning) {
      setIsPositioning(false);
    }
  }, [isOnline, isPositioning]);

  /*
  const getPublicCrashes = useCallback(
    async count => {
      const publicCrashesUrl = new URL(allPublicCrashesUrl)
      publicCrashesUrl.searchParams.set('count', count)
      await sendRequest({
        token
        , url             : publicCrashesUrl
        , manageResData   : res => {
          setPublicCrashList(res.crashes)
        }
      })
    }
  )
    */
  const getPublicCrashes = async () => {
    if (!isOnline) return;
    const res = await httpReq('/PublicAccident/GetList', 'GET', null, false);
    if (!res.responseAnyError) {
      setPublicCrashList(res.responseData);
    }
  };

  const getSafetyIssues = async () => {
    if (!isOnline) return;
    const res = await httpReq('Report/GetList', 'GET', null, true);
    if (!res.responseAnyError) {
      setSafetyIssuesList(res.responseData);
    }
  };

  /*
  const getSafetyIssues = useCallback(
    async () => {
      const safetyIssuesUrl = new URL('http://80.211.22.71:3050/Report/GetList')
        await sendRequest({
        token
        , url             : safetyIssuesUrl
        , manageResData   : res => {
          setSafetyIssuesList(res.responseData)
        }
      })
    }
  )
    */


  /*
  const getSafetyIssues = async () => {
    const res = await httpReq('ReportType/GetList', 'GET', null, true)
    if (!res.responseAnyError) {
      const data = res.responseData
      setSafetyIssuesList(data)
    }
  }
    */

  useEffect(() => {
    if (!isOnline) return;
    getPublicCrashes(0);
  }, [isPublicCrashVis, isOnline]);

  useEffect(() => {
    if (!isOnline) return;
    getSafetyIssues();
  }, [showSafetyIssues, isOnline]);

  useEffect(() => {
    if (!isOnline) return;
    getLayersList();
  }, [isOnline]);

  const getCrashes = useCallback(
    async (format = 'reduced', merged, options = {}) => {
      const allCrashesUrl = new URL(getAllCrashesUrl);
      allCrashesUrl.searchParams.set('format', format);
      if (merged !== undefined) allCrashesUrl.searchParams.append('merged', merged);

      const bbox = options?.bbox;
      if (
        bbox
        && Number.isFinite(Number(bbox.lat0))
        && Number.isFinite(Number(bbox.lat1))
        && Number.isFinite(Number(bbox.lon0))
        && Number.isFinite(Number(bbox.lon1))
      ) {
        const latSpan = Math.abs(Number(bbox.lat1) - Number(bbox.lat0));
        const lonSpan = Math.abs(Number(bbox.lon1) - Number(bbox.lon0));
        const pad = 0.15;
        allCrashesUrl.searchParams.set('lat0', String(Number(bbox.lat0) - latSpan * pad));
        allCrashesUrl.searchParams.set('lat1', String(Number(bbox.lat1) + latSpan * pad));
        allCrashesUrl.searchParams.set('lon0', String(Number(bbox.lon0) - lonSpan * pad));
        allCrashesUrl.searchParams.set('lon1', String(Number(bbox.lon1) + lonSpan * pad));
      }

      const yearsOpt = options?.years;
      if (Array.isArray(yearsOpt) && yearsOpt.length > 0) {
        const validYears = yearsOpt
          .map((y) => Number(y))
          .filter((y) => Number.isFinite(y) && y >= 1);
        if (validYears.length === 1) {
          allCrashesUrl.searchParams.set('year', String(validYears[0]));
        } else if (validYears.length > 1) {
          allCrashesUrl.searchParams.set('years', validYears.join(','));
        }
      } else {
        const year = Number(options?.year);
        if (Number.isFinite(year) && year >= 1) {
          allCrashesUrl.searchParams.set('year', String(year));
        }
      }

      const seq =
        format === 'heat_map' ? ++heatMapFetchSeqRef.current :
        format === 'reduced' ? ++crashFetchSeqRef.current :
        null;
      const res = await request(allCrashesUrl, 'GET', null, { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` });
      if (res.responseAnyError) return;
      if (format === 'heat_map' && seq !== heatMapFetchSeqRef.current) return;
      // Scarta risposte "reduced" obsolete: se l'utente ha toggled Validated/Final
      // più rapidamente di quanto la request precedente completasse, evita che
      // quella vecchia sovrascriva i pin con dati non più corrispondenti al filtro
      // attivo (bug: switch che sembra "invertirsi").
      if (format === 'reduced' && seq !== crashFetchSeqRef.current) return;
      if (format === 'reduced') {
        setAllCrashes(() => ({ reduced: res.responseData.crashes }));
      }
      if (format === 'heat_map') {
        const features = res.responseData?.features ?? [];
        setHeatMapData(() => ({
          type: res.responseData?.type ?? 'FeatureCollection',
          features: features.map((feat) => ({
            ...feat,
            properties: Array.isArray(feat?.properties)
              ? Object.assign({}, ...feat.properties)
              : (feat?.properties ?? {}),
            geometry: {
              ...feat.geometry,
              coordinates: [
              feat.geometry.coordinates[0],
              feat.geometry.coordinates[1]]

            }
          }))
        }));
      }
    },
    [token]
  );

  // Heatmap: all filtered crashes for the selected analysis year (not viewport-scoped).
  // Refetch immediately when year changes while the layer is visible.
  useEffect(() => {
    if (!analysisOn || !heatmapLayerOn || !isOnline) {
      if (!heatmapLayerOn) setHeatMapData(null);
      return undefined;
    }
    if (riskMapYear == null) return undefined;

    setHeatMapData(null);
    getCrashes('heat_map', 1, {
      year: riskMapYear,
    });

    return () => {
      heatMapFetchSeqRef.current += 1;
    };
  }, [analysisOn, heatmapLayerOn, riskMapYear, isOnline, getCrashes, analysisRefreshNonce]);

  /*
  const getCrashes = useCallback(
      async (format = 'reduced', merged) => {
          const allCrashesUrl = new URL(allCrashesBaseUrl)
          allCrashesUrl.searchParams.set('format', format)
          if (merged !== undefined) allCrashesUrl.searchParams.append('merged', merged)
          await sendRequest({
              token
              , url: allCrashesUrl
              , manageResData: res => {
                  if (format === 'reduced') {
                      setAllCrashes(() => ({reduced: res.crashes}))
                  }
                  if (format === 'heat_map') {
                      setHeatMapData(() => ({
                          type: res.type
                          , features: res.features.map(feat => ({
                              ...feat
                              , geometry: {
                                  ...feat.geometry
                                  , coordinates: [
                                      feat.geometry.coordinates[0]
                                      , feat.geometry.coordinates[1]
                                  ]
                              }
                          }))
                      }))
                  }
              }
          })
      },
      [token, sendRequest]
  )
   */

  useEffect(() => {
    const mergeParam = () => {
      if (isAnalysisOnlyAnalyst) return 1;
      if (isMerged && isUnmerged) return undefined;
      if (isMerged) return 1;
      if (isUnmerged) return 0;
    };

    if (!(isMerged || isUnmerged)) {
      // Nessun layer crash attivo: invalida eventuali richieste "reduced" ancora
      // in volo e azzera i dati così, se l'utente riattiva un toggle, non vede
      // per un istante i pin della ricerca precedente prima di quelli nuovi.
      crashFetchSeqRef.current += 1;
      setAllCrashes([]);
      return undefined;
    }

    const yearsForFetch =
      Array.isArray(mapFilterYears) && mapFilterYears.length > 0
        ? mapFilterYears
        : [new Date().getFullYear()];

    getCrashes(undefined, mergeParam(), { years: yearsForFetch });
    return undefined;
  }, [getCrashes, isAnalysisOnlyAnalyst, isMerged, isUnmerged, mapFilterYears]);

  useEffect(() => {
    if (isAnalysisOnlyAnalyst && isUnmerged) {
      setIsUnmerged(false);
    }
  }, [isAnalysisOnlyAnalyst, isUnmerged]);

  useEffect(() => {
    setShowCrashes(isMerged || isUnmerged);
  }, [isMerged, isUnmerged]);

  // Form navigation controls
  const handleSetFormNumber = (number) => {

    if (!isNaN(number)) {

      return setFormNumber(number);
    }
    if (number === '+') {
      const next = formNumber + 1;

      return setFormNumber(next);
    }
    if (number === '-' && formNumber - 1 >= 0) {
      const prev = formNumber - 1;

      return setFormNumber(prev);
    }

    setFormNumber(0);
  };

  const handleSetFormNumberFromList = (formName) => {
    const index = formList.indexOf(formName);

    setFormNumber(index !== -1 ? index : 0);
  };

  const getFormType = useCallback(
    (number) => resolveFormTypeFromPath(formList[number]),
    [formList],
  );

  const getFormDisplayName = useCallback(
    (number) => {
      const section = formModel[getFormType(number)];
      return resolveSectionDisplayLabel(section) || section?.displayName || '';
    },
    [formModel, getFormType],
  );

  const filterObject = (object) => {
    const { isDraft, isFinal, ...rest } = object;
    return rest;
  };

  const activeForm = useMemo(() => {
    const safeNumber =
      formNumber >= 0 && formNumber < formList.length ? formNumber : 0;
    const name = formList[safeNumber] ?? formList[0] ?? 'crash';
    return {
      number: safeNumber,
      name,
      type: getFormType(safeNumber),
      displayName: getFormDisplayName(safeNumber),
    };
  }, [formNumber, formList, getFormType, getFormDisplayName]);

  const navTreeData = useMemo(() => {
    return buildNavTreeStructure(filterObject(methods.getValues()), defaults);
  }, [formList, defaults, methods]);

  // This appends a new instance of repeated form
  const handleAppend = (formType, nestElem) => {
    if (formType === 'units') {
      const lastIndex = unitsFieldArrayMethods.fields.length;
      unitsFieldArrayMethods.append(
        defaults.units.map((elem) => ({ ...elem, 'Unit id': lastIndex + 1 }))
      );
      setFormList((old) => {
        const next = [...old, `${formType}.${lastIndex}`];
        setFormNumber(next.length - 1);
        return next;
      });
      return;
    }
    if (formType === 'people') {
      const lastIndex = peopleFieldArrayMethods.fields.length;
      peopleFieldArrayMethods.append(
        defaults.people.map((elem) => ({
          ...elem,
          'Person id': lastIndex + 1
        }))
      );
      setFormList((old) => {
        const next = [...old, `${formType}.${lastIndex}`];
        setFormNumber(next.length - 1);
        return next;
      });
      return;
    }
    if (formType === 'unitPeople') {
      const unitPath = String(nestElem).split('.').slice(0, 2).join('.');
      const peoplePath = `${unitPath}.people`;
      const currentPeople = methods.getValues(peoplePath) ?? [];
      const nestIndex = currentPeople.length;
      const newEntries = defaults.people.map((elem) => ({
        ...elem,
        'Person id': nestIndex + 1,
        'Unit id': Number.parseInt(unitPath.split('.')[1], 10) + 1,
      }));
      methods.setValue(peoplePath, [...currentPeople, ...newEntries], {
        shouldDirty: true,
        shouldValidate: false,
      });
      const newNestedName = `${unitPath}.people.${nestIndex}`;
      setFormList((old) => {
        const next = [...old, newNestedName];
        setFormNumber(next.length - 1);
        return next;
      });
    }
  };

  const handleRemove = (formType, form) => {
    const { number, name } = form;
    setFormList((old) => old.filter((o) => o !== name));
    if (formType === 'units') {
      const fieldArrayIndex = /(?<=^units\.)\d+/.exec(name)[0];
      unitsFieldArrayMethods.remove(fieldArrayIndex);
      setFormList((old) => old.filter((o) => !o.includes(name)));
    }
    if (formType === 'people') {
      const fieldArrayIndex = /(?<=^people\.)\d+/.exec(name)[0];
      peopleFieldArrayMethods.remove(fieldArrayIndex);
    }
    if (formType === 'unitPeople') {
      const peopleArrayIndex = Number.parseInt(/(?<=people\.)\d+/.exec(name)[0], 10);
      const unitPath = name.split('.').slice(0, 2).join('.');
      const peoplePath = `${unitPath}.people`;
      const currentPeople = methods.getValues(peoplePath) ?? [];
      methods.setValue(
        peoplePath,
        currentPeople.filter((_, index) => index !== peopleArrayIndex),
        { shouldDirty: true, shouldValidate: false },
      );
    }
    setFormNumber(number - 1);
  };

  // Get form data and post it
  // TODO
  const handleCrashSubmit = async (data) => {
    /** Salvataggio solo da dialog (imposta sempre `isFinal: true`); no submit implicito su cambio tab. */
    const resolvedIsFinal =
    data.isFinal === true ||
    data.isFinal === 'true' ||
    methods.getValues('isFinal') === true;
    if (!resolvedIsFinal) return;
    const resolvedIsDraft =
    data.isDraft === true ||
    data.isDraft === 'true' ||
    methods.getValues('isDraft') === true;

    let offlineSaveLoaderActive = false;
    if (!isOnline) {
      if (!canCreateOfflineCrash(user?.type ?? init?.user?.type)) {
        setSeverity('error');
        setMessage(
          _(
            msg`System administrators cannot save crash reports offline. Use an organization account or save while online.`
          )
        );
        return;
      }
      offlineSaveLoaderActive = true;
      setIsOfflineSaving(true);
    }

    let url = insertDraftNewUrl;
    let methodReq = 'POST';
    if (!resolvedIsDraft && resolvedIsFinal) {
      url = newAddCrashUrl;
      methodReq = 'POST';
    }
    if (data.isModifying && resolvedIsFinal) {
      url = data.crash.merge_date ? newAdminModifyCrashUrl : newModifyCrashUrl;
      methodReq = 'PATCH';
    }
    const effectiveOrganization =
    resolveAddCrashOrganizationId() ??
    data.crash?.organization ?? (
    user.organization === -1 ? user.doAsOrganization : user.organization);

    // Send only sections actually shown in the form (formList); fallback to config hierarchy === 0 if formList empty
    const sectionKeysFromFormList = [...new Set(formList.map((p) => String(p).split('.')[0]))];
    const allowedSectionKeys = sectionKeysFromFormList.length > 0 ?
    sectionKeysFromFormList :
    Object.keys(formModel || {}).filter((k) => formModel[k]?.hierarchy === 0);
    const config = formModel || {};
    const crashLabels = getSectionFieldLabels(config, 'crash');
    let crashSnapshot = stampCrashSectionDates(data.crash ?? {});
    try {
      if (!isOnline && typeof navigator !== 'undefined' && navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 25000,
              maximumAge: 0
            });
          });
          crashSnapshot = stampCrashSectionDates({
            ...crashSnapshot,
            Latitude: pos.coords.latitude,
            Longitude: pos.coords.longitude
          });
          persistGpsMetaFromPosition(pos);
        } catch {

          /* keep form coordinates */}
      }
      const compressedImages = await compressCrashImages(images);
      let newData = {
        ...(data.isModifying ? { crash_id: data.crash['Crash id'] } : {}),
        crash: {
          ...pickKeys(crashSnapshot, crashLabels),
          images: compressedImages,
          organization: effectiveOrganization
        }
      };
      if (!data.isModifying) {
        delete newData.crash['Crash id'];
      }
      const peopleLabels = getSectionFieldLabels(config, 'people');
      allowedSectionKeys.forEach((sectionKey) => {
        const canonical = sectionKey.toLowerCase();
        if (canonical === 'road') {
          newData.road = pickKeys(data.road ?? {}, getSectionFieldLabels(config, sectionKey));
        } else if (canonical === 'units') {
          newData.units = (data.units ?? []).map((u) => {
            const unitFiltered = pickKeys(u, getSectionFieldLabels(config, sectionKey));
            if (Array.isArray(u?.people)) {
              unitFiltered.people = u.people.map((p) => pickKeys(p, peopleLabels));
            }
            return unitFiltered;
          });
        } else if (canonical === 'people') {
          newData.people = (data.people ?? []).map((p) => pickKeys(p, peopleLabels));
        }
      });
      newData.isDraft = resolvedIsDraft;
      newData.isFinal = resolvedIsFinal;
      if (data.isModifying != null) newData.isModifying = data.isModifying;
      if (!resolvedIsFinal) {
        newData = { ...newData, draft_suffix: 'auto' };
      }

      newData = normalizeCrashPayloadDates(newData, config);

      const userIdFromCrashGet = crashUserIdFromGetRef.current;
      const userIdFromLoadedCrash =
      data.crash?.user_id ?? data.crash?.userId;
      const resolvedReporterUserId =
      userIdFromCrashGet !== undefined &&
      userIdFromCrashGet !== null &&
      userIdFromCrashGet !== '' ?
      userIdFromCrashGet :
      userIdFromLoadedCrash !== undefined &&
      userIdFromLoadedCrash !== null &&
      userIdFromLoadedCrash !== '' ?
      userIdFromLoadedCrash :
      undefined;
      const insertingUserId = user?.id ?? init?.user?.id;
      if (resolvedReporterUserId !== undefined) {
        newData.crash.user_id = resolvedReporterUserId;
      } else if (!data.isModifying && insertingUserId !== undefined && insertingUserId !== null && insertingUserId !== '') {
        newData.crash.user_id = insertingUserId;
      }

      if (!isOnline) {
        /** Offline dialog offers only "Save offline" (queued sync), not draft. */
        newData.isDraft = false;
        newData.isFinal = true;

        let cu = offlineClientUuidRef.current;
        if (!cu) {
          cu = generateClientUuid();
          offlineClientUuidRef.current = cu;
        }

        try {
          const gpsMeta = localStorage.getItem('adams_last_gps_meta');
          if (gpsMeta) {
            const g = JSON.parse(gpsMeta);
            newData.crash = {
              ...newData.crash,
              _gps_accuracy_m: g.accuracy,
              _gps_captured_at: g.capturedAt
            };
          }
        } catch {

          /* ignore */}

        const localStatus = 'queued';
        await upsertLocalIncident(cu, newData, localStatus);
        await enqueueFinalSync(cu, newData);
        setSeverity('success');
        setMessage(t`Crash queued for sync (offline)`);
        setIsFormOpen(false);
        setIsAdminModifyValidated(false);
        setIsPositioning(false);
        setIsPositionSet(false);
        methods.reset(initFormObj);
        unitsFieldArrayMethods.remove();
        peopleFieldArrayMethods.remove();
        setFormNumber(0);
        setImages(() => []);
        setNewCrash(null);
        crashUserIdFromGetRef.current = null;
        hasFlownRef.current = false;
        setSelectedOrgIdFromDialog(null);
      } else {
        const res = await request(url, methodReq, newData, { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` });
        if (res.responseAnyError) return;
        const serverCrashId = extractServerCrashIdFromResponse(res);
        if (serverCrashId && !data.isModifying && methodReq === 'POST' && url.href === newAddCrashUrl.href) {
          methods.setValue('crash.Crash id', serverCrashId);
        }
        fetchUnreadCount();
        if (resolvedIsFinal) {
          setIsFormOpen(false);
          setIsAdminModifyValidated(false);
          setIsPositioning(false);
          setIsPositionSet(false);
          delete data.isFinal;
          methods.reset(initFormObj);
          unitsFieldArrayMethods.remove();
          peopleFieldArrayMethods.remove();
          setFormNumber(0);
          setImages(() => []);
          setNewCrash(null);
          crashUserIdFromGetRef.current = null;
          hasFlownRef.current = false; // Reset flag quando si salva il crash
          setSelectedOrgIdFromDialog(null);
          // const newMarkers = mapRef.current.getElementsByClassName('new-markers')
          // Array.from(newMarkers).forEach(marker => marker.remove())
          await getCrashes('reduced', 0, {
            years: Array.isArray(mapFilterYears) && mapFilterYears.length > 0 ? mapFilterYears : undefined,
          });
          setIsUnmerged(true);
          setSeverity('success');
          const savedCrashId = serverCrashId ?? data.crash?.['Crash id'] ?? (
            typeof res.responseData === 'string' ? res.responseData : null
          );
          setMessage(
            t`Crash saved ${resolvedIsDraft ? 'as a draft' : `with id ${savedCrashId ?? ''}`}`
          );
        }
      }
    } finally {
      if (offlineSaveLoaderActive) {
        setIsOfflineSaving(false);
      }
    }
  };

  const deleteCrash = async (crashId) => {
    const res = await request(newDeleteCrashUrl, 'POST', { crash: { crash_id: crashId } }, { Authorization: `Bearer ${getAccessTokenFromStorage() || token || ''}` });
    if (res.responseAnyError) return;
    setMessage(t`Crash id ${res.responseData.crash_id || crashId} has been deleted`);
    {










      /*
      sendRequest({
       token
       , url: deleteCrashUrl
       , httpMethod: 'POST'
       , httpBody: { crash_id: crashId }
       , manageResData: res => {
         setMessage(t`Crash id ${res.crash_id} has been deleted`)
       }
      })
      */}};const cancelCrash = (crashId) => {setIsPositionSet(false);setIsFormOpen(false);setIsAdminModifyValidated(false);setIsPositioning(false);setSelectedOrgIdFromDialog(null);setNewCrash(null);crashUserIdFromGetRef.current = null;
    hasFlownRef.current = false; // Reset flag quando si chiude il form
    hasFlownRef.current = false; // Reset flag quando si chiude il form
    // const newMarkers = mapRef.current.getElementsByClassName('new-markers')
    // Array.from(newMarkers).forEach(marker => marker.remove())
    methods.reset(initFormObj); // Resets form
    unitsFieldArrayMethods.remove();
    peopleFieldArrayMethods.remove();
    setImages(() => []);
    setFormNumber(0);
    if (crashId) deleteCrash(crashId);
    // if (isMerged) {
    //   getCrashes(undefined, 1)
    // }
    // if (isUnmerged) {
    //   getCrashes(undefined, 0)
    // }
  };

  const iconSizeStyle = {
    height: 20,
    width: 20
  };



  const subPanelElements = useMemo(() => {
    const crashLayerSwitches = [
      {
        label: isAnalysisOnlyAnalyst ? t`Crashes on map` : t`Validated crashes`,
        icon: null,
        type: 'switch',
        name: 'crashes',
        onMobile: true,
        access: [0, 1, 2, 3],
        // Lo stato reale (isMerged) è la fonte di verità: l'azione deve
        // rispecchiare esattamente il valore dello switch (checked), non
        // invertire ciecamente lo stato precedente. In caso contrario un
        // secondo toggle rapido, o un cambio di isMerged da un altro punto
        // dell'app, desincronizza lo switch visuale dal filtro realmente
        // applicato ai pin.
        checked: isMerged,
        action: (isChecked) => setIsMerged(Boolean(isChecked)),
      },
      ...(!isAnalysisOnlyAnalyst ? [{
        label: t`Final crashes`,
        icon: null,
        type: 'switch',
        name: 'crashes',
        onMobile: true,
        access: [0, 1, 2, 3],
        checked: isUnmerged,
        action: (isChecked) => setIsUnmerged(Boolean(isChecked)),
      }] : []),
    ];

    return [
      {
        label:
          isPositioning || isFormOpen ?
            t`Cancel` :
            t`Add crash`,
        icon:
          isPositioning || isFormOpen ?
            <SvgCustomIcon name={'cancel'} /> :
            <SvgCustomIcon name={'addLocation'} />,
        type: 'button',
        onMobile: false,
        access: [0, 1, 2, 3],
        baseOrgAccess: [1, 2, 3],
        action:
          isPositioning || isFormOpen ?
            () => cancelCrash() :
            handleOpenAddCrash,
      },
      ...crashLayerSwitches,
      ...(analysisAvailable ? [{
        label: t`Spatial analysis`,
        icon: null,
        type: 'switch',
        name: 'analysis',
        onMobile: true,
        access: [0, 1, 2, 3],
        action: (isChecked) => {
          if (!isChecked) {
            heatMapFetchSeqRef.current += 1;
            setHeatmapLayerOn(false);
            setRiskMapLayerOn(false);
            setHeatMapData(null);
          }
          onVisibilityChange('analysis', isChecked);
        },
      }] : []),
      {
        label: t`Legend`,
        icon: null,
        type: 'switch',
        name: 'legend',
        onMobile: true,
        access: [0, 1, 2, 3],
        action: (isChecked) => {
          setShowLegend(isChecked);
        },
      },
    ];
  }, [
    analysisAvailable,
    handleOpenAddCrash,
    isAnalysisOnlyAnalyst,
    isFormOpen,
    isMerged,
    isPositioning,
    isUnmerged,
    onVisibilityChange,
  ]);


  const togglePublicCrashVis = useCallback((val) => {
    setIsPublicCrashVis(val);
  }, []);

  const toggleSafetyIssuesVis = useCallback((val) => {
    setShowSafetyIssues(val);
  }, []);

  const initCommunitySwitches = useMemo(() => [
  {
    label: t`Citizen crashes`,
    icon: null,
    type: 'switch',
    name: 'publicCrashes',
    onMobile: true,
    access: [1],
    checked: false,
    action: (isChecked) => togglePublicCrashVis(isChecked)
  },
  {
    label: t`Safety issues`,
    icon: null,
    type: 'switch',
    name: 'safetyIssues',
    onMobile: true,
    access: [1],
    checked: false,
    action: (isChecked) => toggleSafetyIssuesVis(isChecked)
  }],
  [togglePublicCrashVis, toggleSafetyIssuesVis]);

  const [communitySwitches, setCommunitySwitches] = useState(initCommunitySwitches);

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportScope, setExportScope] = useState(
    /** @type {'all' | 'new_only'} */'all'
  );

  const openExportDialog = useCallback((scope = 'all') => {
    setExportScope(scope === 'new_only' ? 'new_only' : 'all');
    setExportDialogOpen(true);
  }, []);

  const runOfflineZipExport = useCallback(async (scope) => {
    const rows = await listLocalIncidents();
    const exportable = filterLocalIncidentsForExport(rows, scope);
    if (exportable.length === 0) {
      setSeverity('warning');
      if (scope === 'all') {
        setMessage(
          _(
            msg`No offline incidents to export. Save reports with “Save offline” first (drafts on device only are not included).`
          )
        );
      } else {
        setMessage(
          _(
            msg`No incidents waiting for export. Everything stored offline was already included in a previous ZIP.`
          )
        );
      }
      return;
    }
    const orgResult = resolveSingleSourceOrganization(exportable);
    if (!orgResult.ok) {
      setSeverity('error');
      setMessage(
        orgResult.code === 'ORG_MISSING' ?
        _(msg`Each incident must have an organization to export a server-compatible ZIP.`) :
        _(msg`All incidents in the export must belong to the same organization.`)
      );
      return;
    }
    const createdByRaw = user?.id ?? init?.user?.id;
    if (createdByRaw == null || createdByRaw === '') {
      setSeverity('error');
      setMessage(_(msg`Cannot export: user id is missing.`));
      return;
    }
    const packageId = generateClientUuid();
    const exportedAt = new Date().toISOString();
    const manifest = buildManifestJson({
      sourceOrganizationId: orgResult.organizationId,
      createdByUserId: Number(createdByRaw),
      packageId,
      exportedAt
    });
    const zip = new JSZip();
    zip.file('manifest.json', JSON.stringify(manifest, null, 2));
    const crashesFolder = zip.folder('crashes');
    for (let index = 0; index < exportable.length; index += 1) {
      const r = exportable[index];
      const name = sanitizeCrashEntryFilename(index, r.clientUuid);
      const entryStem = getCrashEntryStem(name);
      const { jsonPayload, attachmentFiles } = await prepareCrashPayloadForZipExport(
        r.payload,
        formModel,
        entryStem
      );
      crashesFolder.file(name, JSON.stringify(jsonPayload, null, 2));
      attachmentFiles.forEach(({ zipPath, bytes }) => {
        zip.file(zipPath, bytes);
      });
    }
    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    saveAs(blob, `crash-package-${packageId}.zip`);
    for (const r of exportable) {
      await markIncidentExported(r.clientUuid);
    }
    const bearer = getAccessTokenFromStorage();
    if (bearer && bearer !== '__offline__' && typeof navigator !== 'undefined' && navigator.onLine) {
      postPackageExportRegistration(manifest, bearer).catch(() => {});
    }
    setSeverity('success');
    setMessage(t`Export completed`);
  }, [_, formModel, init?.user?.id, user?.id]);

  const importZipFileRef = useRef(null);
  const [importZipBusy, setImportZipBusy] = useState(false);

  const handleImportZipClick = useCallback(() => {
    importZipFileRef.current?.click();
  }, []);

  const handleImportZipFile = useCallback(
    async (e) => {
      const input = e.target;
      const file = input.files?.[0];
      input.value = '';
      if (!file) return;

      const bearer = getAccessTokenFromStorage();
      if (!bearer || bearer === '__offline__') {
        setSeverity('error');
        setMessage(_(msg`Sign in online to import crash packages.`));
        return;
      }

      setImportZipBusy(true);
      try {
        const { status, body } = await postCrashZipImport(file, bearer);
        if (status === 200) {
          const summary = body?.summary;
          if (summary != null && typeof summary === 'object') {
            const { imported, duplicatesSkipped, errors } = summary;
            const lines = [];
            if (imported != null) lines.push(`${_(msg`Imported`)}: ${imported}`);
            if (duplicatesSkipped != null) {
              lines.push(`${_(msg`Duplicates skipped`)}: ${duplicatesSkipped}`);
            }
            if (errors != null) lines.push(`${_(msg`Errors`)}: ${errors}`);
            setMessage(lines.length > 0 ? lines.join('\n') : _(msg`Import finished.`));
            const errCount = Number(errors);
            setSeverity(
              Number.isFinite(errCount) && errCount > 0 ? 'warning' : 'success'
            );
          } else {
            setSeverity('success');
            setMessage(_(msg`Import finished.`));
          }
        } else if (status === 413) {
          setSeverity('error');
          const max = body?.maxZipArchiveBytes;
          setMessage(
            max != null ?
            _(msg`ZIP file exceeds maximum size`) + ` (${max} bytes).` :
            _(msg`ZIP file exceeds maximum size.`)
          );
        } else if (status === 429) {
          setSeverity('error');
          setMessage(_(msg`Too many import requests. Try again later.`));
        } else if (status === 400 || status === 401 || status === 403) {
          setSeverity('error');
          const errMsg = body?.message;
          setMessage(
            typeof errMsg === 'string' && errMsg ?
            errMsg :
            _(msg`Import failed`) + ` (${status})`
          );
        } else {
          setSeverity('error');
          setMessage(_(msg`Import failed`) + ` (${status})`);
        }
      } catch (err) {
        setSeverity('error');
        setMessage(err instanceof Error ? err.message : _(msg`Import failed.`));
      } finally {
        setImportZipBusy(false);
      }
    },
    [_]
  );

  const userType = Number(init?.user?.type ?? user?.type);
  /** Organization admin (2) e multi-org admin (3); il system admin (1) non importa ZIP da qui. */
  const canImportCrashZipArchive = userType === 2 || userType === 3;
  const canShowPrepareDevice = isOnline && canProvisionDevice(userType);
  const canExportLocalData = canExportLocalCrashData(userType);
  const canUseOfflineCrashTools = canCreateOfflineCrash(userType);
  const showOfflinePwaTools =
    (canExportLocalData && !isOnline) ||
    canImportCrashZipArchive ||
    canShowPrepareDevice;
  const offlinePwaNavProps = showOfflinePwaTools ?
  {
    isOnline,
    ...(canExportLocalData && { onExport: openExportDialog }),
    ...(canImportCrashZipArchive && isOnline && {
      onImportZip: handleImportZipClick,
      importZipBusy
    }),
    ...(canShowPrepareDevice && {
      onPrepareDevice: () =>
      navigate('/device-provision', {
        state: { ...location.state, token, init, isHistoryOpen: false }
      })
    })
  } :
  null;

  const offlineNavActionsRegistration = useMemo(
    () =>
    canExportLocalData || (canImportCrashZipArchive && isOnline) ?
    {
      isOnline,
      ...(canExportLocalData && { onExport: openExportDialog }),
      ...(canImportCrashZipArchive &&
      isOnline && {
        onImportZip: handleImportZipClick,
        importZipBusy
      })
    } :
    null,
    [
    canExportLocalData,
    canImportCrashZipArchive,
    isOnline,
    openExportDialog,
    handleImportZipClick,
    importZipBusy]

  );

  useEffect(() => {
    const exportFlag = location.state?.openOfflineExport
    if (!exportFlag || !canExportLocalData) return

    const scope = exportFlag === 'new_only' ? 'new_only' : 'all'
    openExportDialog(scope)

    const nextState = { ...(location.state || {}) }
    delete nextState.openOfflineExport
    navigate('/home', { state: nextState, replace: true })
  }, [
    location.state?.openOfflineExport,
    canExportLocalData,
    openExportDialog,
    navigate,
    location.state,
  ])

  useRegisterOfflineNavActions(offlineNavActionsRegistration);

  return (
    <Box component="main">
      <input
        ref={importZipFileRef}
        type="file"
        accept=".zip,application/zip"
        style={{ display: 'none' }}
        aria-hidden
        onChange={handleImportZipFile} />
      
      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        aria-labelledby="offline-export-dialog-title"
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 'var(--radius-2xl, 12px)',
              overflow: 'hidden'
            }
          }
        }}>
        
        <DialogTitle
          id="offline-export-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 2,
            px: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor:
            theme.palette.mode === 'light' ?
            'var(--color-background, #F8FAFC)' :
            'transparent',
            color: theme.palette.newColors?.gray || theme.palette.text.primary,
            fontSize: 'var(--font-lg, 14px)',
            fontWeight: 700
          }}>
          
          <FolderZipOutlinedIcon sx={{ color: theme.palette.primary.main }} />
          {_(msg`Export local crashes`)}
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <DialogContentText
            component="div"
            sx={{
              color: theme.palette.text.secondary,
              mb: 2,
              fontSize: 'var(--font-md, 13px)'
            }}>
            
            {_(msg`Select the offline crash reports to include in the ZIP file`)}
          </DialogContentText>
          <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
            <RadioGroup
              value={exportScope}
              onChange={(e) =>
              setExportScope(/** @type {'all' | 'new_only'} */e.target.value)
              }>
              
              <Stack spacing={1.25}>
                {[
                {
                  value: 'all',
                  title: _(msg`All saved offline reports`),
                  description: _(
                    msg`Include all crash reports saved offline on this device`
                  )
                },
                {
                  value: 'new_only',
                  title: _(msg`Reports not yet exported`),
                  description: _(
                    msg`Include only crash reports that have not already been exported from this device. Reports already exported are excluded.`
                  )
                }].
                map((option) => {
                  const selected = exportScope === option.value;
                  return (
                    <Box
                      key={option.value}
                      onClick={() => setExportScope(option.value)}
                      sx={{
                        borderRadius: 'var(--radius-lg, 8px)',
                        border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                        bgcolor: selected ?
                        theme.palette.mode === 'light' ?
                        'rgba(99, 102, 241, 0.08)' :
                        'rgba(99, 102, 241, 0.16)' :
                        'transparent',
                        px: 1,
                        py: 0.5,
                        cursor: 'pointer',
                        transition: 'border-color 0.15s ease, background-color 0.15s ease',
                        '&:hover': {
                          bgcolor:
                          theme.palette.mode === 'light' ?
                          'rgba(99, 102, 241, 0.04)' :
                          'rgba(255, 255, 255, 0.04)'
                        }
                      }}>
                      
                      <FormControlLabel
                        value={option.value}
                        control={
                        <Radio
                          size="small"
                          sx={{
                            color: theme.palette.text.secondary,
                            '&.Mui-checked': { color: theme.palette.primary.main }
                          }} />

                        }
                        label={
                        <Box sx={{ py: 0.5 }}>
                            <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: 'var(--font-md, 13px)',
                              color: theme.palette.text.primary
                            }}>
                            
                              {option.title}
                            </Typography>
                            <Typography
                            variant="caption"
                            component="div"
                            sx={{
                              display: 'block',
                              mt: 0.25,
                              color: theme.palette.text.secondary,
                              lineHeight: 1.4
                            }}>
                            
                              {option.description}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          alignItems: 'flex-start',
                          m: 0,
                          width: '100%',
                          '& .MuiFormControlLabel-label': { flex: 1 }
                        }} />
                      
                    </Box>);

                })}
              </Stack>
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor:
            theme.palette.mode === 'light' ?
            'var(--color-background, #F8FAFC)' :
            'transparent'
          }}>
          
          <Button
            variant="outlined"
            onClick={() => setExportDialogOpen(false)}
            sx={{
              color: theme.palette.newColors?.gray || theme.palette.text.secondary,
              borderColor: theme.palette.divider
            }}>
            
            {_(msg`Cancel`)}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setExportDialogOpen(false);
              void runOfflineZipExport(exportScope);
            }}
            sx={{
              background:
              theme.palette.newColors?.cyanGradient || theme.palette.primary.main,
              color: '#fff',
              fontWeight: 600,
              '&:hover': {
                background:
                theme.palette.newColors?.cyanGradient || theme.palette.primary.dark
              }
            }}>
            
            {_(msg`Export`)}
          </Button>
        </DialogActions>
      </Dialog>
      {isMobile ?
      <>
          <MobileTopNav offlinePwaProps={offlinePwaNavProps} />
          <MobileTopNavSpacer />
        </> :

      <>
            <DesktopToolbar />
            <DesktopNav
          position={position}
          subPanelElements={subPanelElements}
          isStatsPanelOpen={isStatsPanelOpen}
          isDrawingPolygon={isDrawingPolygon} />
        
          </>
      }
      <Box sx={isMobile ? {} : { marginInlineStart: NAV_LAYOUT_MARGIN_START }}>
        {isFormOpen &&
        <FormProvider {...methods} {...{ user }} {...{ handlePicture }}>
            <FormContainer
            token={token}
            hydratedSessionInit={hydratedInit}
            open={isFormOpen}
            isLoading={isLoading}
            isMobile={isMobile}
            formModel={formModel}
            formList={formList}
            form={activeForm}
            submitRef={submitRef}
            handleFormOpen={handleFormOpen}
            handleSetFormNumber={handleSetFormNumber}
            handleCrashSubmit={handleCrashSubmit}
            handleAppend={handleAppend}
            handleRemove={handleRemove}
            images={images}
            reporterData={reporterData}
            cancelCrash={cancelCrash}
            isAdminModifyValidated={isAdminModifyValidated}
            onActionLogDrawerOpenChange={setIsActionLogDrawerOpen} />
          


            {!isMobile && !isActionLogDrawerOpen &&
          <NaviTreeView
            init={init.configuration}
            defaults={defaults}
            data={navTreeData}
            handleSetFormNumber={handleSetFormNumberFromList} />

          }

          </FormProvider>
        }
        {effectiveHistoryOpen &&
        <History
          position={position}
          open={effectiveHistoryOpen}
          isMobile={isMobile}
          getCrash={getCrash}
          onClose={() => {
            // Close form if open
            if (isFormOpen) {
              cancelCrash();
            }
            // Close History
            navigate('/home', {
              state: {
                ...(location.state || {}),
                token,
                ...(hydratedInit?.configuration ? { init: hydratedInit } : {}),
                isHistoryOpen: false
              }
            });
          }} />

        }

        <Snackbar
          open={!!error && isOnline}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          TransitionComponent={SlideTransition}
          onClose={(_, reason) => {
            if (reason === 'clickaway') {
              return;
            }
            setMessage('');
            setError('');
          }}>
          
          <Alert variant="filled" severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          TransitionComponent={SlideTransition}
          onClose={(_, reason) => {
            if (reason === 'clickaway') {
              return;
            }
            setMessage('');
          }}>
          
          <Alert
            variant="filled"
            severity={severity || 'error'}
            sx={{
              width: '100%',
              ...(String(message).includes('\n') ? { whiteSpace: 'pre-line' } : {})
            }}>
            
            {message}
          </Alert>
        </Snackbar>

        {isOnline ?
        <MapLibre
          isMobile={isMobile}
          canDrawPolygon={canDrawPolygon}
          communitySwitches={communitySwitches}
          setCommunitySwitches={setCommunitySwitches}
          isPositioning={isPositioning}
          isFormOpen={isFormOpen}
          isHistoryOpen={effectiveHistoryOpen}
          onStatsPanelOpen={() => setIsStatsPanelOpen(true)}
          onStatsPanelClose={() => setIsStatsPanelOpen(false)}
          onDrawingStart={() => setIsDrawingPolygon(true)}
          onDrawingEnd={() => setIsDrawingPolygon(false)}
          showCrashes={showCrashes}
          isMerged={isMerged}
          isUnmerged={isUnmerged}
          showPublicCrashes={isPublicCrashVis}
          showSafetyIssues={showSafetyIssues}
          allCrashes={allCrashes}
          publicCrashes={publicCrashList}
          safetyIssuesList={safetyIssuesList}
          mapRef={mapRef}
          crashesSummary={totCrashes}
          heatMapData={heatMapData}
          roadRiskMapData={roadRiskMapData}
          roadRiskMapMetadata={roadRiskMapMetadata}
          roadRiskMapPrevScores={roadRiskMapPrevScores}
          roadNetworkMapData={roadNetworkMapData}
          roadNetworkUnderlayEnabled={roadNetworkUnderlayEnabled}
          onRoadNetworkUnderlayEnabledChange={setRoadNetworkUnderlayEnabled}
          riskMapLoading={riskMapLoading}
          riskMapYear={riskMapYear}
          riskMapYears={riskMapYears}
          mapFilterYears={mapFilterYears}
          onMapFilterYearsChange={setMapFilterYears}
          onRiskMapRefresh={refreshRiskMapLayers}
          onRiskMapDemoSeed={seedRiskMapDemoCrashes}
          riskMapDemoSeedLoading={riskMapDemoSeedLoading}
          heatmapLayerOn={heatmapLayerOn}
          onHeatmapLayerOnChange={setHeatmapLayerOn}
          riskMapLayerOn={riskMapLayerOn}
          onRiskMapLayerOnChange={setRiskMapLayerOn}
          onMapBoundsChange={handleMapBoundsChange}
          newCrash={newCrash}
          getCrash={getCrash}
          layersVisibility={layersVisibility}
          handlePositioningState={handlePositioningState}
          handleIsPositionSetState={handleIsPositionSetState}
          isPositionSet={isPositionSet}
          handleCoordinates={handleCoordinates}
          handleFormOpen={handleFormOpen}
          handleSetNewCrash={handleSetNewCrash}
          handleCrashToEdit={handleCrashToEdit}
          handleOwnPosCoords={handleOwnPosCoords}
          layersList={layersList}
          setLayersList={setLayersList}
          canReorderMapLayers={canReorderMapLayers}
          onMoveLayerUp={handleMoveLayerUp}
          onMoveLayerDown={handleMoveLayerDown}
          isMapLayerReordering={isMapLayerReordering}
          shouldTriggerGeoControl={shouldTriggerGeoControl} /> :


        <OfflineHomeMapPlaceholder
          isMobile={isMobile}
          isFormOpen={isFormOpen}
          isHistoryOpen={effectiveHistoryOpen} />

        }
        <Dialog
          open={isAdminDialogOpen}
          onClose={() => {
            closeAdminDialog();
          }}>
          
          <DialogTitle>
            <Trans>Choose the organization</Trans>
          </DialogTitle>
          <List sx={{ pt: 0 }}>
            <ListItem disableGutters>
              <ListItemButton
                onClick={async () => {
                  await getNewInit(1);
                  closeAdminDialog();
                  openDialog();
                }}>
                
                <ListItemAvatar>
                  <Avatar>
                    <LocalPolice />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={_(msg`Police`)} />
              </ListItemButton>
            </ListItem>
            <ListItem disableGutters>
              <ListItemButton
                onClick={async () => {
                  await getNewInit(2);
                  closeAdminDialog();
                  openDialog();
                }}>
                
                <ListItemAvatar>
                  <Avatar>
                    <LocalHospital />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={_(msg`Healthcare`)} />
              </ListItemButton>
            </ListItem>
            <ListItem disableGutters>
              <ListItemButton
                onClick={async () => {
                  await getNewInit(3);
                  closeAdminDialog();
                  openDialog();
                }}>
                
                <ListItemAvatar>
                  <Avatar>
                    <AccountBalance />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={_(msg`Insurance`)} />
              </ListItemButton>
            </ListItem>
          </List>
        </Dialog>
        <Dialog open={isAddDialogOpen} onClose={closeDialog}>
          <DialogTitle>
            {(() => {
              const managedOrgIds = Array.isArray(init?.user?.organization_ids) ? init.user.organization_ids : [];
              const userOrgId = init?.user?.organization != null ? Number(init.user.organization) : null;
              const orgIdsToShow = [...new Set([...managedOrgIds.map(Number), userOrgId].filter((id) => id != null && !Number.isNaN(id) && id !== -1))];
              const userType = user.type ?? init?.user?.type;
              const isMultiOrg = (userType === 2 || userType === 3) && orgIdsToShow.length > 0;
              const allOrganizations = init?.organizations ?? [];
              const managedOrgs = allOrganizations.filter((o) => orgIdsToShow.includes(Number(o.id ?? o.value)));
              const allowedBasicIds = [...new Set(managedOrgs.map((o) => Number(o.basic_id ?? o.basic)).filter((b) => [1, 2, 3].includes(b)))];
              const hasOrgOptions = allowedBasicIds.length > 0;
              const showChooseOrg = isMultiOrg && addDialogStep === 'choose_org' && hasOrgOptions;
              return showChooseOrg ? _(msg`Choose report type`) : <Trans>What do you want to do?</Trans>;
            })()}
          </DialogTitle>
          <List sx={{ pt: 0 }}>
            {(() => {
              const managedOrgIds = Array.isArray(init?.user?.organization_ids) ? init.user.organization_ids : [];
              const userOrgId = init?.user?.organization != null ? Number(init.user.organization) : null;
              const orgIdsToShow = [...new Set([...managedOrgIds.map(Number), userOrgId].filter((id) => id != null && !Number.isNaN(id) && id !== -1))];
              const userType = user.type ?? init?.user?.type;
              const isMultiOrg = (userType === 2 || userType === 3) && orgIdsToShow.length > 0;
              const allOrganizations = init?.organizations ?? [];
              const managedOrgs = allOrganizations.
              filter((o) => orgIdsToShow.includes(Number(o.id ?? o.value)));
              const allowedBasicIds = [...new Set(managedOrgs.map((o) => Number(o.basic_id ?? o.basic)).filter((b) => [1, 2, 3].includes(b)))];
              const managedOrgsByBasic = managedOrgs.filter((o) => allowedBasicIds.includes(Number(o.basic_id ?? o.basic)));
              const basicIdToOrgId = {};
              allowedBasicIds.forEach((basicId) => {
                const org = managedOrgsByBasic.find((o) => Number(o.basic_id ?? o.basic) === basicId);
                if (org) basicIdToOrgId[basicId] = org.id ?? org.value;
              });
              const orgOptions = [
              { basicId: 1, label: _(msg`Police`), Icon: LocalPolice },
              { basicId: 2, label: _(msg`Healthcare`), Icon: LocalHospital },
              { basicId: 3, label: _(msg`Insurance`), Icon: AccountBalance }].
              filter((o) => allowedBasicIds.includes(o.basicId));
              const showChooseOrg = isMultiOrg && addDialogStep === 'choose_org' && orgOptions.length > 0;
              /** Police / Healthcare / Insurance: in offline servono GPS e “senza coordinate” (prima era solo org 2). */
              const effectiveBasicOrg = Number(
                user.organization === -1 ? user.doAsOrganization : user.organization
              );
              const showOfflineCrashEntryActions =
              canUseOfflineCrashTools &&
              !Number.isNaN(effectiveBasicOrg) && [1, 2, 3].includes(effectiveBasicOrg);

              if (showChooseOrg) {
                return (
                  <>
                    {orgOptions.map(({ basicId, label, Icon }) =>
                    <ListItem key={basicId} disableGutters>
                        <ListItemButton
                        onClick={async () => {
                          const orgId = basicIdToOrgId[basicId];
                          setSelectedOrgIdFromDialog(orgId);
                          await getNewInit(orgId);
                          setAddDialogStep('actions');
                        }}>
                        
                          <ListItemAvatar>
                            <Avatar>
                              <Icon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText primary={label} />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </>);

              }

              return (
                <>
            {showOfflineCrashEntryActions &&
                  <>
                {!isOnline &&
                    <ListItem disableGutters>
                    <ListItemButton
                        onClick={handleAddCrashOfflineWithGps}
                        disabled={isAcquiringGps}>
                        
                      <ListItemAvatar>
                        <Avatar>
                          <GpsFixedIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                          primary={
                          isAcquiringGps ?
                          _(msg`Acquiring GPS...`) :
                          _(msg`Add new crash with GPS`)
                          } />
                        
                    </ListItemButton>
                  </ListItem>
                    }
                <ListItem disableGutters>
                  <ListItemButton
                        onClick={() => {
                          closeDialog();
                          handleCoordinates({ lat: 0, lng: 0 });
                          crashUserIdFromGetRef.current = null;
                          setNewCrash({ isNew: true, Latitude: 0, Longitude: 0 });
                          setIsFormOpen(true);
                        }}>
                        
                    <ListItemAvatar>
                      <Avatar>
                        <AddCircleIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                          primary={
                          isOnline ?
                          _(msg`Add new crash without coordinates`) :
                          _(msg`Add without coordinates`)
                          } />
                        
                  </ListItemButton>
                </ListItem>
              </>
                  }
            {isOnline &&
                  <ListItem disableGutters>
                <ListItemButton
                      onClick={() => {
                        closeDialog();
                        crashUserIdFromGetRef.current = null;
                        setIsPositioning(true);
                      }}>
                      
                  <ListItemAvatar>
                    <Avatar>
                      <AddLocationAltIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText primary={_(msg`Add new crash on map`)} />
                </ListItemButton>
              </ListItem>
                  }
            {ownPosCoords && <ListItem disableGutters>
              <ListItemButton
                      onClick={() => {
                        closeDialog();
                        crashUserIdFromGetRef.current = null;
                        setNewCrash({
                          isNew: true,
                          Longitude: ownPosCoords.lng,
                          Latitude: ownPosCoords.lat
                        });
                        handleCoordinates({ ...ownPosCoords });
                        setIsFormOpen(true);
                      }}>
                      
                <ListItemAvatar>
                  <Avatar>
                    <AddLocationIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                        primary={_(msg`Add new crash at your current location`)} />
                      
              </ListItemButton>
            </ListItem>}
            {showDraftsInAddDialog &&
                  <ListItem disableGutters>
              <ListItemButton
                      onClick={() => {
                        setIsDraftsMenuOpen(true);
                        getAllDrafts();
                      }}>
                      
                <ListItemAvatar>
                  <Avatar>
                    <EditLocationAltIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={_(msg` Edit a crash from drafts`)} />
              </ListItemButton>
            </ListItem>
                  }
            {showDraftsInAddDialog &&
                  <Collapse in={isDraftsMenuOpen} timeout="auto" unmountOnExit>
              <Divider />
              <List component="div" disablePadding>
                {allDrafts.length ?

                      allDrafts.map((draft) => {
                        // Handle both old format (YYYYMMDDThhmmssSS) and new format (1_20251202144727_auto)
                        let label = draft.replace(/^\d_/, '').replace('p', '+');
                        let dateStr;

                        // Check if it's the new format (ends with _auto or _manual)
                        if (label.includes('_auto') || label.includes('_manual')) {
                          // Extract timestamp: "20251202144727_auto" -> "20251202144727"
                          const timestamp = label.replace(/_auto$/, '').replace(/_manual$/, '');
                          // Parse as "YYYYMMDD HHmmss"
                          dateStr = dayjs(timestamp, 'YYYYMMDDHHmmss').format('DD/MM/YYYY HH:mm:ss');
                        } else {
                          // Old format
                          dateStr = dayjs(label, 'YYYYMMDDThhmmssSS').format('DD/MM/YYYY HH:mm:ss');
                        }

                        return (
                          <ListItemButton
                            key={draft}
                            sx={{ pl: 6 }}
                            onClick={() => getDraft(draft)}>
                            
                          <ListItemText primary={dateStr} />
                        </ListItemButton>);

                      }) :


                      <ListItemText sx={{ pl: 6 }} primary={t`No drafts saved`} />
                      }
              </List>
              <Divider />
              <Typography pl={6} mt={1} mb={1}><Trans>Auto-saved drafts (last five)</Trans></Typography>
              <Divider />
              <List component="div" disablePadding>
                {allAutoDrafts.length ?

                      allAutoDrafts.map((draft) => {
                        // Handle both old format (YYYYMMDDThhmmssSS) and new format (1_20251202144727_auto)
                        let label = draft.replace(/^\d_/, '').replace('p', '+');
                        let dateStr;

                        // Check if it's the new format (ends with _auto or _manual)
                        if (label.includes('_auto') || label.includes('_manual')) {
                          // Extract timestamp: "20251202144727_auto" -> "20251202144727"
                          const timestamp = label.replace(/_auto$/, '').replace(/_manual$/, '');
                          // Parse as "YYYYMMDD HHmmss"
                          dateStr = dayjs(timestamp, 'YYYYMMDDHHmmss').format('DD/MM/YYYY HH:mm:ss');
                        } else {
                          // Old format
                          dateStr = dayjs(label, 'YYYYMMDDThhmmssSS').format('DD/MM/YYYY HH:mm:ss');
                        }

                        return (
                          <ListItemButton
                            key={draft}
                            sx={{ pl: 6 }}
                            onClick={() => getDraft(draft)}>
                            
                          <ListItemText primary={dateStr} />
                        </ListItemButton>);

                      }) :


                      <ListItemText sx={{ pl: 6 }} primary={t`No drafts saved`} />
                      }
              </List>
            </Collapse>
                  }
                </>);

            })()}
          </List>
        </Dialog>
      </Box>
      {isMobile && !isFormOpen && isPositionSet &&
      <Box
        pt={1}
        width='100%'
        position='absolute'
        bottom={theme.mixins.toolbar.minHeight}
        height={theme.mixins.toolbar.minHeight}
        zIndex={theme.zIndex.drawer}
        sx={{
          backgroundColor: theme.palette.background.default
          // , opacity: 0.9
          , textAlign: 'center',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px'
        }}>
        
          <IconButton onClick={() => setIsFormOpen(true)}>
            <KeyboardDoubleArrowUpIcon />
          </IconButton>
        </Box>
      }
      {isMobile &&
      <MobileBottomNav
        openDialog={openDialog}
        openAdminDialog={openAdminDialog}
        onOpenAddCrash={handleOpenAddCrash}
        handleFormOpen={handleFormOpen}
        isPositioning={isPositioning}
        isFormOpen={isFormOpen}
        isPositionSet={isPositionSet}
        cancelCrash={cancelCrash}
        handlePositioningState={handlePositioningState}
        openMobileLayers={openMobileLayers}
        toggleMobileLayers={toggleMobileLayers} />

      }
      {isMobile &&
      <MobileLayersPanel
        open={openMobileLayers}
        subPanelElements={subPanelElements} />

      }
      <FullPageProgress
        isOpen={isLoading || isAcquiringGps || isOfflineSaving || importZipBusy}
        isMobile={isMobile}
        isFormOpen={isFormOpen} />
      
      {showLegend && <Legend isMobile={isMobile} />}
    </Box>);

}